import {
  Add,
  ArrowDropDown,
  Close,
  Delete,
  Email,
  Http,
  LinkedIn,
  MarkunreadMailbox,
  PlayArrow,
  RecordVoiceOver,
  Textsms,
  WebAsset,
  Person,
  Phone,
  PlaylistAdd,
  PlaylistAddCheck,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Slider,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import DOMPurify from "dompurify";
import React, { useEffect, useState } from "react";
import { Bot } from "lucide-react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";
import CrmIntegrationSelect from "../../components/CrmIntegrationSelect";
import EmailTemplateEditor from "../../components/EmailTemplateEditor";
import { HeyGenVideoConfig } from "../../components/HeyGenVideoDialog";
import { LandingPageConfig } from "../../components/LandingPageDialog";
import LoadingButton from "../../components/LoadingButton";
import MultiCollaboratorSelect from "../../components/MultiCollaboratorSelect";
import PopOutTextInput from "../../components/PopOutTextInput";
import SearchableSenderProfileSelect, {
  loadAllSenderProfiles,
} from "../../components/SearchableSenderProfileSelect";
import {
  MESSAGE_LENGTH_LIMITS,
  SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION,
  SEQUENCE_ACTION_TYPE_BROWSER_TASK,
  SEQUENCE_ACTION_TYPE_EMAIL,
  SEQUENCE_ACTION_TYPE_HTTP_REQUEST,
  SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST,
  SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE,
  SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT,
  SEQUENCE_ACTION_TYPE_META_ADS_AUDIENCE,
  SEQUENCE_ACTION_TYPE_AUTO_AGENT,
  SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN,
  SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN,
  SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN,
  SEQUENCE_ACTION_TYPE_SMS,
  SEQUENCE_ACTION_TYPE_THANKSIO,
  SEQUENCE_ACTION_TYPE_VOICE_MESSAGE,
  SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE,
  SEQUENCE_ACTION_TYPE_ADD_TO_LIST,
  DUPLICATE_HANDLING_OVERWRITE,
  DUPLICATE_HANDLING_SKIP,
  DUPLICATE_HANDLING_RESET,
  DUPLICATE_HANDLING_ALLOW_DUPLICATES,
  CONVERSATION_OUTCOMES,
  AB_TEST_DEFAULT_VARIANT_NAME,
} from "../../constants";
import ABTestEditor, {
  SequenceStepVariant,
} from "./components/ABTestEditor";
import { VariableField } from "../../components/PopOutTextDialog";
import SendScheduleEditor from "../../components/SendScheduleEditor";
import { hostname } from "../../utils";
import SequenceActions from "./components/SequenceActions";
import SequenceActivityView from "./components/SequenceActivityView";
import SequenceHttpRequestForm from "./SequenceHttpRequestForm";
import SequenceMetaAdsForm from "./SequenceMetaAdsForm";
import SequenceAutoAgentForm from "./SequenceAutoAgentForm";
import SequenceThanksioForm from "./SequenceThanksioForm";
import SequenceMembersView from "./components/SequenceMembersView";
import SequenceMetrics from "./components/SequenceMetrics";

export default ({
  open,
  onClose,
  sequence,
  companyId,
  getToken,
  eventListener,
  superUser = false,
}: {
  open: boolean;
  onClose: () => void;
  sequence: any;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener?: (event: any) => void;
  superUser?: boolean;
}) => {
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  const [steps, setSteps] = useState<any[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [selectedStep, setSelectedStep] = useState<any>(null);
  const [profiles, setProfiles] = useState<any>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [senderProfileId, setSenderProfileId] = useState(""); // Legacy - keep for backward compatibility
  const [senderProfileIds, setSenderProfileIds] = useState<string[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [includeUnsubscribeLink, setIncludeUnsubscribeLink] = useState(true);
  const [autoSend, setAutoSend] = useState(false);
  const [removeOnReply, setRemoveOnReply] = useState(false);
  const [trackOpens, setTrackOpens] = useState(true);
  const [trackClicks, setTrackClicks] = useState(true);
  const [sendAsTextOnly, setSendAsTextOnly] = useState(false);
  const [validateEmails, setValidateEmails] = useState(false);
  const [prospectValue, setProspectValue] = useState<number>(1000);
  const [linkedinIntegrations, setLinkedinIntegrations] = useState<Array<any>>(
    []
  );
  const [linkedinIntegrationIds, setLinkedinIntegrationIds] = useState<
    string[]
  >([]);

  // Phone/Twilio settings (for SMS and future voice actions)
  const [twilioIntegrations, setTwilioIntegrations] = useState<Array<any>>([]);
  const [phoneIntegrationIds, setPhoneIntegrationIds] = useState<string[]>([]);
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState<
    Array<any>
  >([]);
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>(
    []
  );
  const [voices, setVoices] = useState<Array<any>>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>("");
  const [removeOnOutcomes, setRemoveOnOutcomes] = useState<string[]>([]);

  // Browser session integrations (for browser tasks)
  const [browserIntegrations, setBrowserIntegrations] = useState<Array<any>>(
    []
  );

  // Prosp integrations (for Add to Prosp Campaign)
  const [prospIntegrations, setProspIntegrations] = useState<Array<any>>([]);
  // Salesforge integrations (for Add to Salesforge Campaign)
  const [salesforgeIntegrations, setSalesforgeIntegrations] = useState<Array<any>>([]);
  const [salesforgeWorkspaces, setSalesforgeWorkspaces] = useState<
    Record<string, Array<any>>
  >({});
  const [salesforgeSequences, setSalesforgeSequences] = useState<
    Record<string, Array<any>>
  >({});
  const [salesforgeLoadingWorkspaces, setSalesforgeLoadingWorkspaces] =
    useState<Record<string, boolean>>({});
  const [salesforgeLoadingSequences, setSalesforgeLoadingSequences] = useState<
    Record<string, boolean>
  >({});

  // HeyReach integrations (for Add to HeyReach Campaign)
  const [heyreachIntegrations, setHeyreachIntegrations] = useState<Array<any>>(
    []
  );

  // Thanks.io integrations (for direct mail)
  const [thanksioIntegrations, setThanksioIntegrations] = useState<Array<any>>(
    []
  );

  // Sequences and lists (for "Add to Sequence" / "Add to List" steps)
  const [availableSequences, setAvailableSequences] = useState<Array<any>>([]);
  const [availableLists, setAvailableLists] = useState<Array<any>>([]);

  // Meta Ads integrations (for custom audiences)
  const [metaAdsIntegrations, setMetaAdsIntegrations] = useState<Array<any>>(
    []
  );

  // Scheduling integrations (for calendar booking tracking)
  const [schedulingIntegrations, setSchedulingIntegrations] = useState<
    Array<any>
  >([]);

  // HeyGen integrations (for video generation in email templates)
  const [heygenIntegrations, setHeygenIntegrations] = useState<Array<any>>([]);

  // Agents (for agent conversation steps)
  const [agents, setAgents] = useState<Array<any>>([]);

  // CRM sync settings for reply management
  const [crmSyncEnabled, setCrmSyncEnabled] = useState(false);
  const [crmIntegrationId, setCrmIntegrationId] = useState("");

  // Notification settings - which team members receive notifications
  const [notificationUserIds, setNotificationUserIds] = useState<string[]>([]);

  // LinkedIn automation settings - always default to backend_automated
  const linkedinAutomationMethod = "backend_automated";
  const [addStepAnchorEl, setAddStepAnchorEl] = useState<null | HTMLElement>(
    null
  );

  // Test step state
  const [testStepDialogOpen, setTestStepDialogOpen] = useState(false);
  const [testStepLoading, setTestStepLoading] = useState(false);
  const [testStepResult, setTestStepResult] = useState<any>(null);
  const [testStepMemberId, setTestStepMemberId] = useState("");
  const [testStepSelectedMember, setTestStepSelectedMember] =
    useState<any>(null);
  const [useTestUser, setUseTestUser] = useState(true);
  const [sequenceMembers, setSequenceMembers] = useState<any[]>([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [mainTabValue, setMainTabValue] = useState(0);

  // Scheduling settings - each day has its own time window
  const [scheduleSettings, setScheduleSettings] = useState<{
    timezone: string;
    [key: string]: any;
  }>({
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York", // Default to browser timezone
    monday: { enabled: true, startTime: "09:00", endTime: "17:00" },
    tuesday: { enabled: true, startTime: "09:00", endTime: "17:00" },
    wednesday: { enabled: true, startTime: "09:00", endTime: "17:00" },
    thursday: { enabled: true, startTime: "09:00", endTime: "17:00" },
    friday: { enabled: true, startTime: "09:00", endTime: "17:00" },
    saturday: { enabled: false, startTime: "09:00", endTime: "17:00" },
    sunday: { enabled: false, startTime: "09:00", endTime: "17:00" },
  });


  // LinkedIn rate limits (max 30 each)
  const [linkedinProfileVisitLimit, setLinkedinProfileVisitLimit] =
    useState(10);
  const [linkedinConnectionRequestLimit, setLinkedinConnectionRequestLimit] =
    useState(5);
  const [linkedinMessageLimit, setLinkedinMessageLimit] = useState(8);

  // Email rate limit (per day per email address)
  const [emailRateLimit, setEmailRateLimit] = useState(2);

  // Email sender limit (per day per sender account)
  const [emailSenderLimit, setEmailSenderLimit] = useState(15);

  // SMS rate limit (per day per phone number)
  const [smsRateLimit, setSmsRateLimit] = useState(2);
  const [active, setActive] = useState(true);

  // Custom userData fields for variable insertion
  const [customUserDataFields, setCustomUserDataFields] = useState<
    { key: string; label: string }[]
  >([]);

  // Standard member fields for variable insertion
  const standardMemberFields: VariableField[] = [
    { key: "firstName", label: "First Name", icon: <Person fontSize="small" /> },
    { key: "lastName", label: "Last Name", icon: <Person fontSize="small" /> },
    { key: "email", label: "Email", icon: <Email fontSize="small" /> },
    { key: "phone", label: "Phone", icon: <Phone fontSize="small" /> },
    { key: "linkedinUrl", label: "LinkedIn URL", icon: <LinkedIn fontSize="small" /> },
  ];

  // Custom fields as VariableField type
  const customVariableFieldsTyped: VariableField[] = customUserDataFields.map((field) => ({
    key: field.key,
    label: field.label,
    isCustom: true,
  }));

  // Get the max length based on action type
  const getMaxLengthForActionType = (actionType: string): number | undefined => {
    switch (actionType) {
      case SEQUENCE_ACTION_TYPE_SMS:
        return MESSAGE_LENGTH_LIMITS.SMS_MAX;
      case SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST:
        return MESSAGE_LENGTH_LIMITS.LINKEDIN_CONNECTION_REQUEST;
      case SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE:
        return MESSAGE_LENGTH_LIMITS.LINKEDIN_MESSAGE;
      default:
        return undefined;
    }
  };

  const daysOfWeek = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];

  const handleAddStep = (actionType = SEQUENCE_ACTION_TYPE_EMAIL) => {
    const isEmail = actionType === SEQUENCE_ACTION_TYPE_EMAIL;
    const isSms = actionType === SEQUENCE_ACTION_TYPE_SMS;
    const isVoice = actionType === SEQUENCE_ACTION_TYPE_VOICE_MESSAGE;
    const isBrowserTask = actionType === SEQUENCE_ACTION_TYPE_BROWSER_TASK;
    const isProspCampaign = actionType === SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN;
    const isHeyReachCampaign =
      actionType === SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN;
    const isSalesforgeCampaign =
      actionType === SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN;
    const isHttpRequest = actionType === SEQUENCE_ACTION_TYPE_HTTP_REQUEST;
    const isAgentConversation =
      actionType === SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION;
    const isMetaAdsAudience =
      actionType === SEQUENCE_ACTION_TYPE_META_ADS_AUDIENCE;
    const isAutoAgent = actionType === SEQUENCE_ACTION_TYPE_AUTO_AGENT;
    const isAddToSequence =
      actionType === SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE;
    const isAddToList = actionType === SEQUENCE_ACTION_TYPE_ADD_TO_LIST;
    const emailSteps = steps.filter(
      (s: any) => s.actionType === SEQUENCE_ACTION_TYPE_EMAIL
    );
    const stepName = isEmail
      ? `New Email ${emailSteps.length + 1}`
      : `${getActionTypeLabel(actionType)} ${
          steps.filter((s) => s.actionType === actionType).length + 1
        }`;
    const newStep = {
      id: uuidv4(),
      actionType: actionType,
      stepName: stepName,
      subject:
        isEmail && emailSteps.length > 0 ? "<previous-email-subject>" : "", // Subject is only used for emails and will be AI generated if aiPersonalized is true
      content: "",
      daysAfterPrevious: 0,
      order: steps.length + 1,
      aiPersonalized: isEmail || isSms || isVoice || isAgentConversation,
      aiPersonalizedInstructions: "",
      linkedinAction:
        !isEmail &&
        !isSms &&
        !isVoice &&
        !isBrowserTask &&
        !isProspCampaign &&
        !isSalesforgeCampaign &&
        !isHttpRequest &&
        !isAgentConversation &&
        !isMetaAdsAudience &&
        !isAddToSequence &&
        !isAddToList
          ? {}
          : null,
      actionParameters: isBrowserTask
        ? {
            sessionType: "none",
            browserIntegrationId: null,
            zipcode: null,
          }
        : isProspCampaign
        ? {
            prospIntegrationId: null,
            listId: "",
            campaignId: "",
          }
        : isSalesforgeCampaign
        ? {
            salesforgeIntegrationId: null,
            workspaceId: "",
            sequenceId: "",
          }
        : isHttpRequest
        ? {
            method: "POST",
            url: "",
            queryParams: "",
            body: "{}",
            headers: '{"Content-Type": "application/json"}',
          }
        : isAgentConversation
        ? {
            agentId: null,
            channel: "voice",
            conversationContext: "",
          }
        : isMetaAdsAudience
        ? {
            integrationInstanceId: null,
            audienceId: "",
            includePhone: true,
            includeName: true,
          }
        : isAddToSequence
        ? {
            targetSequenceId: "",
            duplicateHandling: DUPLICATE_HANDLING_OVERWRITE,
          }
        : isAddToList
        ? {
            targetListId: "",
          }
        : {},
    };
    setSteps([...steps, newStep]);
    setSelectedStep(newStep);
  };

  const getActionTypeLabel = (actionType: string) => {
    switch (actionType) {
      case SEQUENCE_ACTION_TYPE_EMAIL:
        return "Email";
      case SEQUENCE_ACTION_TYPE_SMS:
        return "SMS";
      case SEQUENCE_ACTION_TYPE_VOICE_MESSAGE:
        return "Voice Message";
      case SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION:
        return "Agent Conversation";
      case SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT:
        return "LinkedIn Profile Visit";
      case SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST:
        return "LinkedIn Connection Request";
      case SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE:
        return "LinkedIn Message";
      case SEQUENCE_ACTION_TYPE_BROWSER_TASK:
        return "Browser Task";
      case SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN:
        return "Add to Prosp Campaign";
      case SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN:
        return "Add to HeyReach Campaign";
      case SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN:
        return "Add to Salesforge Campaign";
      case SEQUENCE_ACTION_TYPE_HTTP_REQUEST:
        return "HTTP Request";
      case SEQUENCE_ACTION_TYPE_THANKSIO:
        return "Thanks.io Direct Mail";
      case SEQUENCE_ACTION_TYPE_META_ADS_AUDIENCE:
        return "Add to Meta Ads Audience";
      case SEQUENCE_ACTION_TYPE_AUTO_AGENT:
        return "Auto Agent Loop";
      case SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE:
        return "Add to Sequence";
      case SEQUENCE_ACTION_TYPE_ADD_TO_LIST:
        return "Add to List";
      default:
        return "Unknown";
    }
  };

  const getActionTypeIcon = (actionType: string) => {
    switch (actionType) {
      case SEQUENCE_ACTION_TYPE_SMS:
        return <Textsms sx={{ fontSize: 20 }} />;
      case SEQUENCE_ACTION_TYPE_VOICE_MESSAGE:
        return <RecordVoiceOver sx={{ fontSize: 20 }} />;
      case SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION:
        return <Bot size={20} />;
      case SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT:
      case SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST:
      case SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE:
        return <LinkedIn sx={{ fontSize: 20 }} />;
      case SEQUENCE_ACTION_TYPE_BROWSER_TASK:
        return <WebAsset sx={{ fontSize: 20 }} />;
      case SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN:
        return (
          <img
            src={require("../../img/prosp.png")}
            alt="Prosp"
            style={{ width: 20, height: 20 }}
          />
        );
      case SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN:
        return (
          <img
            src={require("../../img/heyreach.png")}
            alt="HeyReach"
            style={{ width: 20, height: 20 }}
          />
        );
      case SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN:
        return (
          <img
            src={require("../../img/salesforge.svg")}
            alt="Salesforge"
            style={{ width: 20, height: 20 }}
          />
        );
      case SEQUENCE_ACTION_TYPE_HTTP_REQUEST:
        return <Http sx={{ fontSize: 20 }} />;
      case SEQUENCE_ACTION_TYPE_THANKSIO:
        return <MarkunreadMailbox sx={{ fontSize: 20 }} />;
      case SEQUENCE_ACTION_TYPE_META_ADS_AUDIENCE:
        return (
          <img
            src="https://logos.composio.dev/api/metaads"
            alt="Meta Ads"
            style={{ width: 20, height: 20 }}
          />
        );
      case SEQUENCE_ACTION_TYPE_AUTO_AGENT:
        return <Bot size={20} />;
      case SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE:
        return <PlaylistAddCheck sx={{ fontSize: 20 }} />;
      case SEQUENCE_ACTION_TYPE_ADD_TO_LIST:
        return <PlaylistAdd sx={{ fontSize: 20 }} />;
      default:
        return <Email sx={{ fontSize: 20 }} />;
    }
  };

  const handleSaveStep = (updatedStep: any) => {
    setSteps(
      steps.map((step: any) =>
        step.id === updatedStep.id
          ? { ...updatedStep, order: updatedStep.order ?? step.order }
          : step
      )
    );
    setSelectedStep(updatedStep);
  };

  const handleDeleteStep = () => {
    setSteps(steps.filter((step: any) => step.id !== selectedStep.id));
    setSelectedStep(null);
    setOpenDeleteDialog(false);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const newSteps = Array.from(steps);
    const [reorderedItem] = newSteps.splice(result.source.index, 1);
    newSteps.splice(result.destination.index, 0, reorderedItem);
    const updatedSteps = newSteps.map((step: any, index: number) => ({
      ...step,
      order: index + 1,
    }));
    setSteps(updatedSteps);
  };

  const handleClose = () => {
    // Reset all state when closing the dialog
    setName("");
    setId("");
    setSteps([]);
    setSelectedStep(null);
    setSenderProfileId("");
    setSenderProfileIds([]);
    setSelectedProfile(null);
    setIncludeUnsubscribeLink(true);
    setAutoSend(false);
    setRemoveOnReply(false);
    setTrackOpens(true);
    setTrackClicks(true);
    setLinkedinIntegrationIds([]);
    setPhoneIntegrationIds([]);
    setSelectedPhoneNumbers([]);
    setActive(true);
    setProspectValue(1000);

    // Reset schedule settings to defaults
    setScheduleSettings({
      timezone:
        Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
      monday: { enabled: true, startTime: "09:00", endTime: "17:00" },
      tuesday: { enabled: true, startTime: "09:00", endTime: "17:00" },
      wednesday: { enabled: true, startTime: "09:00", endTime: "17:00" },
      thursday: { enabled: true, startTime: "09:00", endTime: "17:00" },
      friday: { enabled: true, startTime: "09:00", endTime: "17:00" },
      saturday: { enabled: false, startTime: "09:00", endTime: "17:00" },
      sunday: { enabled: false, startTime: "09:00", endTime: "17:00" },
    });

    // Reset LinkedIn rate limits to defaults
    setLinkedinProfileVisitLimit(10);
    setLinkedinConnectionRequestLimit(5);
    setLinkedinMessageLimit(8);

    // Reset email and phone rate limits to defaults
    setEmailRateLimit(2);
    setSmsRateLimit(2);

    // Reset main tab to Builder
    setMainTabValue(0);

    onClose();
  };

  const toggleSequenceActive = async (activeStatus: boolean) => {
    if (!id) return; // Can't toggle if sequence doesn't exist yet

    const currentToken = await getToken();
    try {
      await axios.post(
        `${hostname}/sequences`,
        {
          id,
          name,
          steps,
          companyId,
          active: activeStatus,
          senderProfileId: senderProfileId,
          senderProfileIds: senderProfileIds,
          linkedinIntegrationIds: linkedinIntegrationIds,
          linkedinAutomationSettings: {
            method: linkedinAutomationMethod,
          },
          phoneIntegrationIds: phoneIntegrationIds,
          phoneNumbers: selectedPhoneNumbers,
          voiceId: selectedVoiceId || null,
          includeUnsubscribeLink,
          autoSend,
          removeOnReply,
          trackOpens,
          trackClicks,
          sendAsTextOnly,
          validateEmails,
          scheduleSettings,
          linkedinProfileVisitLimit,
          linkedinConnectionRequestLimit,
          linkedinMessageLimit,
          emailRateLimit,
          emailSenderLimit,
          smsRateLimit,
          prospectValue,
          crmSyncEnabled,
          crmIntegrationId: crmSyncEnabled ? crmIntegrationId : null,
          notificationUserIds,
          removeOnOutcomes,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      setActive(activeStatus);
    } catch (err) {
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: "Failed to update sequence status",
        });
      }
    }
  };

  const getSenderProfiles = async () => {
    setProfilesLoading(true);
    try {
      const currentToken = await getToken();
      const profilesData = await loadAllSenderProfiles(currentToken, companyId);
      setProfiles(profilesData);

      // If current senderProfileId doesn't exist in profiles, reset it
      if (
        senderProfileId &&
        !profilesData.find((p: any) => p.id === senderProfileId)
      ) {
        setSenderProfileId("");
      }

      // Filter out any invalid profile IDs from the multi-select array
      const validProfileIds = senderProfileIds.filter((id) =>
        profilesData.find((p: any) => p.id === id)
      );
      if (validProfileIds.length !== senderProfileIds.length) {
        setSenderProfileIds(validProfileIds);
      }
    } finally {
      setProfilesLoading(false);
    }
  };

  const getLinkedInIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/browser_session`, {
        params: { companyId, type: "linkedin_login" },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        // Use the specific endpoint for LinkedIn integrations
        const linkedinIntegrations = res.data.items || [];
        setLinkedinIntegrations(linkedinIntegrations);
      })
      .catch((err) => {
        console.log("Failed to fetch LinkedIn integrations:", err);
      });
  };

  const getBrowserIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/browser_session`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const browserIntegrations = res.data.items || [];
        setBrowserIntegrations(browserIntegrations);
      })
      .catch((err) => {
        setBrowserIntegrations([]);
      });
  };

  const getSchedulingIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/scheduling`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const integrations = res.data.items || [];
        setSchedulingIntegrations(integrations);
      })
      .catch((err) => {
        setSchedulingIntegrations([]);
      });
  };

  const getHeygenIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/video`, {
        params: { companyId, type: "heygen" },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const integrations = res.data.items || [];
        setHeygenIntegrations(integrations);
      })
      .catch((err) => {
        setHeygenIntegrations([]);
      });
  };

  const getAvailableSequences = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/sequences`, {
        params: { companyId, page: 1, pageSize: 100 },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setAvailableSequences(res.data.items || []);
      })
      .catch(() => {
        setAvailableSequences([]);
      });
  };

  const getAvailableLists = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/lists`, {
        params: { companyId, page: 1, pageSize: 100, minimal: true },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setAvailableLists(res.data.items || []);
      })
      .catch(() => {
        setAvailableLists([]);
      });
  };

  const getProspIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/prosp`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const integrations = res.data.items || [];
        setProspIntegrations(integrations);
      })
      .catch((err) => {
        setProspIntegrations([]);
      });
  };

  const getSalesforgeIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/salesforge`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const integrations = res.data.items || [];
        setSalesforgeIntegrations(integrations);
      })
      .catch((err) => {
        setSalesforgeIntegrations([]);
      });
  };

  const getSalesforgeWorkspaces = async (integrationId: string) => {
    if (!integrationId || salesforgeWorkspaces[integrationId]) return;
    setSalesforgeLoadingWorkspaces((prev) => ({ ...prev, [integrationId]: true }));
    try {
      const currentToken = await getToken();
      const res = await axios.post(
        `${hostname}/integrations/salesforge/workspaces`,
        { companyId, integrationId },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      setSalesforgeWorkspaces((prev) => ({
        ...prev,
        [integrationId]: res.data?.data || [],
      }));
    } catch (err) {
      setSalesforgeWorkspaces((prev) => ({ ...prev, [integrationId]: [] }));
    } finally {
      setSalesforgeLoadingWorkspaces((prev) => ({ ...prev, [integrationId]: false }));
    }
  };

  const getSalesforgeSequences = async (
    integrationId: string,
    workspaceId: string
  ) => {
    if (!integrationId || !workspaceId) return;
    const key = `${integrationId}:${workspaceId}`;
    if (salesforgeSequences[key]) return;
    setSalesforgeLoadingSequences((prev) => ({ ...prev, [key]: true }));
    try {
      const currentToken = await getToken();
      const res = await axios.post(
        `${hostname}/integrations/salesforge/sequences`,
        { companyId, integrationId, workspaceId },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      console.log("Salesforge sequences response:", res.data);
      setSalesforgeSequences((prev) => ({
        ...prev,
        [key]: res.data?.sequences || [],
      }));
    } catch (err: any) {
      console.error(
        "Salesforge sequences fetch failed:",
        err?.response?.data || err?.message || err
      );
      setSalesforgeSequences((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setSalesforgeLoadingSequences((prev) => ({ ...prev, [key]: false }));
    }
  };

  const getHeyreachIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/heyreach`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const integrations = res.data.items || [];
        setHeyreachIntegrations(integrations);
      })
      .catch((err) => {
        setHeyreachIntegrations([]);
      });
  };

  const getThanksioIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/thanksio`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const integrations = res.data.items || [];
        setThanksioIntegrations(integrations);
      })
      .catch((err) => {
        setThanksioIntegrations([]);
      });
  };

  const getMetaAdsIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/meta_ads`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const integrations = res.data.items || [];
        setMetaAdsIntegrations(integrations);
      })
      .catch((err) => {
        setMetaAdsIntegrations([]);
      });
  };

  const getTwilioIntegrations = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/integrations/by-type/sms`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const twilioIntegrations = res.data.items || [];
        setTwilioIntegrations(twilioIntegrations);
        // Fetch phone numbers when integrations are loaded
        fetchPhoneNumbers(twilioIntegrations);
      })
      .catch((err) => {
        console.log("Failed to fetch Twilio integrations:", err);
      });
  };

  const fetchPhoneNumbers = async (integrations: any[]) => {
    const currentToken = await getToken();
    const allPhoneNumbers: any[] = [];

    // Fetch phone numbers from each Twilio integration
    for (const integration of integrations) {
      try {
        const response = await axios.get(
          `${hostname}/integrations/twilio/${integration.id}/phone-numbers`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${currentToken}` },
          }
        );

        const phoneNumbers = response.data.phoneNumbers || [];
        // Add integration info to each phone number
        phoneNumbers.forEach((number: any) => {
          allPhoneNumbers.push({
            ...number,
            integrationId: integration.id,
            integrationName:
              integration.instanceName || integration.name || "Twilio Account",
          });
        });
      } catch (error) {
        console.error(
          `Failed to fetch phone numbers for integration ${integration.id}:`,
          error
        );
      }
    }

    setAvailablePhoneNumbers(allPhoneNumbers);
  };

  const getVoices = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/voices`, {
        params: { companyId, includePublic: true },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const voicesData = res.data.items || [];
        setVoices(voicesData);
      })
      .catch((err) => {
        console.error("Failed to fetch voices:", err);
        setVoices([]);
      });
  };

  const getAgents = async () => {
    const currentToken = await getToken();
    return axios
      .get(`${hostname}/agents`, {
        params: { companyId, pageSize: 100 },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const agentsData = res.data.items || [];
        setAgents(agentsData);
      })
      .catch((err) => {
        console.error("Failed to fetch agents:", err);
        setAgents([]);
      });
  };

  const getSequenceMembers = async (searchQuery = "") => {
    if (!id || id.startsWith("demo-")) return;
    setMemberSearchLoading(true);
    const currentToken = await getToken();
    try {
      const response = await axios.get(`${hostname}/sequences/${id}/members`, {
        params: {
          companyId,
          page: 1,
          pageSize: 20,
          search: searchQuery || undefined,
        },
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      setSequenceMembers(response.data.items || []);
    } catch (err) {
      console.error("Failed to fetch sequence members:", err);
    } finally {
      setMemberSearchLoading(false);
    }
  };

  const handleTestStep = async () => {
    if ((!testStepMemberId && !useTestUser) || !selectedStep) return;
    setTestStepLoading(true);
    const currentToken = await getToken();
    try {
      const response = await axios.post(
        `${hostname}/sequences/${id}/steps/${selectedStep.id}/test`,
        {
          companyId,
          memberId: useTestUser ? undefined : testStepMemberId,
          useTestUser: useTestUser,
          // Pass current editor state so user doesn't have to save first
          subject: selectedStep.subject,
          content: selectedStep.content,
          aiPersonalized: selectedStep.aiPersonalized,
          aiPersonalizedInstructions: selectedStep.aiPersonalizedInstructions,
          actionType: selectedStep.actionType,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      setTestStepResult(response.data);
    } catch (err: any) {
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: err.response?.data?.error || "Failed to test step",
        });
      }
    } finally {
      setTestStepLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testStepResult || !selectedStep) return;
    setSendingTestEmail(true);
    const currentToken = await getToken();
    try {
      const response = await axios.post(
        `${hostname}/sequences/${id}/steps/${selectedStep.id}/test/send`,
        {
          companyId,
          testEmail: testEmail,
          subject: testStepResult.subject,
          content: testStepResult.content,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      if (response.data.success) {
        if (eventListener) {
          eventListener({
            type: "SET_SUCCESS",
            message: `Test email sent successfully to ${testEmail}`,
          });
        }
      }
    } catch (err: any) {
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: err.response?.data?.error || "Failed to send test email",
        });
      }
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleCloseTestDialog = () => {
    setTestStepDialogOpen(false);
    // Reset all test-related state
    setTestStepResult(null);
    setTestStepMemberId("");
    setTestStepSelectedMember(null);
    setUseTestUser(false);
    setMemberSearchQuery("");
    setTestStepLoading(false);
    setTestEmail("");
    setSendingTestEmail(false);
  };

  // Load available fields for variable insertion when sequence has an ID
  const loadAvailableFields = async () => {
    if (!id || id.startsWith("demo-")) return;
    const currentToken = await getToken();
    try {
      const response = await axios.get(
        `${hostname}/sequences/${id}/available-fields`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      const fields =
        response.data.customFields?.map((f: any) => ({
          key: f.key,
          label: f.label,
        })) || [];
      setCustomUserDataFields(fields);
    } catch (err) {
      // Silently fail - custom fields are optional
      console.error("Failed to load available fields:", err);
    }
  };

  const onSubmit = async () => {
    const currentToken = await getToken();
    setLoading(true);

    // Use default name if empty
    const sequenceName =
      name.trim() || `Untitled ${new Date().toLocaleDateString()}`;

    // Update the name state if it was empty
    if (!name.trim()) {
      setName(sequenceName);
    }

    return axios
      .post(
        `${hostname}/sequences`,
        {
          id,
          name: sequenceName,
          steps,
          companyId,
          active,
          senderProfileId: senderProfileId, // Legacy field
          senderProfileIds: senderProfileIds,
          linkedinIntegrationIds: linkedinIntegrationIds,
          linkedinAutomationSettings: {
            method: linkedinAutomationMethod,
          },
          phoneIntegrationIds: phoneIntegrationIds,
          phoneNumbers: selectedPhoneNumbers,
          voiceId: selectedVoiceId || null,
          includeUnsubscribeLink,
          autoSend,
          removeOnReply,
          trackOpens,
          trackClicks,
          sendAsTextOnly,
          validateEmails,
          scheduleSettings,
          linkedinProfileVisitLimit,
          linkedinConnectionRequestLimit,
          linkedinMessageLimit,
          emailRateLimit,
          emailSenderLimit,
          smsRateLimit,
          prospectValue,
          crmSyncEnabled,
          crmIntegrationId: crmSyncEnabled ? crmIntegrationId : null,
          notificationUserIds,
          removeOnOutcomes,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      )
      .then((res) => {
        // Dialog stays open after save
        // Update the ID if this was a new sequence (backend returns the created sequence with ID)
        if (res.data && res.data.sequence && res.data.sequence.id && !id) {
          setId(res.data.sequence.id);
        }

        // Sync frontend state with what was actually saved in the backend
        // This ensures linkedinIntegrationIds, voiceId, phoneIntegrationIds, etc. match database
        if (res.data && res.data.sequence) {
          const savedSequence = res.data.sequence;
          if (savedSequence.linkedinIntegrationIds) {
            setLinkedinIntegrationIds(savedSequence.linkedinIntegrationIds);
          }
          if (savedSequence.phoneIntegrationIds) {
            setPhoneIntegrationIds(savedSequence.phoneIntegrationIds);
          }
          if (savedSequence.voiceId) {
            setSelectedVoiceId(savedSequence.voiceId);
          }
          if (savedSequence.senderProfileIds) {
            setSenderProfileIds(savedSequence.senderProfileIds);
          }
        }
        setSaveSuccess(true);
      })
      .catch((err) => {
        console.error("Failed to save sequence:", err);
        if (eventListener) {
          eventListener({
            type: "SET_ERROR",
            error:
              "Failed to save sequence. Please check your connection and try again.",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      getSenderProfiles();
      getLinkedInIntegrations();
      getTwilioIntegrations();
      getBrowserIntegrations();
      getSchedulingIntegrations();
      getHeygenIntegrations();
      getProspIntegrations();
      getHeyreachIntegrations();
      getSalesforgeIntegrations();
      getThanksioIntegrations();
      getMetaAdsIntegrations();
      getVoices();
      getAgents();
      getAvailableSequences();
      getAvailableLists();

      // Set default name for new sequences (when sequence is null or has no name)
      if (!sequence || !sequence.name) {
        setName(`Untitled ${new Date().toLocaleDateString()}`);
      }

      // Load sequence members if editing existing sequence
      if (id) {
        getSequenceMembers();
        loadAvailableFields();
      }
    }
  }, [open, id]);

  // Auto-fetch Salesforge workspaces/sequences when a Salesforge step is selected
  useEffect(() => {
    if (
      selectedStep?.actionType === SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN
    ) {
      const integrationId =
        selectedStep.actionParameters?.salesforgeIntegrationId;
      const workspaceId = selectedStep.actionParameters?.workspaceId;
      if (integrationId) {
        getSalesforgeWorkspaces(integrationId);
        if (workspaceId) {
          getSalesforgeSequences(integrationId, workspaceId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedStep?.id,
    selectedStep?.actionParameters?.salesforgeIntegrationId,
    selectedStep?.actionParameters?.workspaceId,
  ]);

  // Keyboard navigation for step selection
  useEffect(() => {
    if (!open || mainTabValue !== 0 || steps.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle arrow keys when not typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const currentIndex = selectedStep
        ? steps.findIndex((s) => s.id === selectedStep.id)
        : -1;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        // Move to next step
        if (currentIndex < steps.length - 1) {
          setSelectedStep(steps[currentIndex + 1]);
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        // Move to previous step
        if (currentIndex > 0) {
          setSelectedStep(steps[currentIndex - 1]);
        } else if (currentIndex === -1 && steps.length > 0) {
          // If no step selected, select the first one
          setSelectedStep(steps[0]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, mainTabValue, steps, selectedStep]);

  // Debounced search for sequence members
  useEffect(() => {
    if (!id || !testStepDialogOpen) return;

    const delayDebounceFn = setTimeout(() => {
      getSequenceMembers(memberSearchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [memberSearchQuery, id, testStepDialogOpen]);

  useEffect(() => {
    if (sequence) {
      setId(sequence.id);
      setName(sequence.name);
      // The list endpoint no longer returns steps (they bloated the payload),
      // so for an existing sequence steps are owned entirely by the detail
      // fetch below — this effect must NOT touch `steps` for an existing
      // sequence. The `sequence` prop reference can change on any parent
      // re-render (e.g. list refresh), and since it never carries `steps`
      // anymore, resetting them here would clobber the fetched/edited steps
      // and the next save would persist an empty steps array (deleting them).
      // Only seed a placeholder step for a brand-new sequence (no id yet).
      if (!sequence.id) {
        const initialSteps = sequence.steps || [
          {
            id: uuidv4(),
            actionType: SEQUENCE_ACTION_TYPE_EMAIL,
            stepName: "New Email 1",
            subject: "",
            content: "",
            daysAfterPrevious: 0,
            order: 1,
            aiPersonalized: true,
            aiPersonalizedInstructions: "",
          },
        ];
        const processedSteps = initialSteps.map((step: any) => {
          const processedStep = {
            ...step,
            actionType: step.actionType || SEQUENCE_ACTION_TYPE_EMAIL,
          };

          // If it's a LinkedIn connection request with no content, set sendWithNote to false
          if (
            processedStep.actionType ===
              SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST &&
            !processedStep.content &&
            !processedStep.aiPersonalizedInstructions
          ) {
            processedStep.sendWithNote = false;
          }

          return processedStep;
        });
        setSteps(processedSteps);
        if (processedSteps.length > 0) {
          setSelectedStep(processedSteps[0]);
        }
      }
      setSenderProfileId(sequence.senderProfileId || "");

      // Load multiple sender profiles
      if (
        sequence.senderProfileIds &&
        Array.isArray(sequence.senderProfileIds)
      ) {
        setSenderProfileIds(sequence.senderProfileIds);
      } else if (sequence.senderProfileId) {
        // Migrate from single profile to array format
        setSenderProfileIds([sequence.senderProfileId]);
      } else {
        setSenderProfileIds([]);
      }
      // Load multiple LinkedIn integrations
      if (
        sequence.linkedinIntegrationIds &&
        Array.isArray(sequence.linkedinIntegrationIds)
      ) {
        setLinkedinIntegrationIds(sequence.linkedinIntegrationIds);
      } else if (sequence.linkedinIntegrationId || sequence.linkedinAccountId) {
        // Migrate from single integration to array format
        setLinkedinIntegrationIds([
          sequence.linkedinIntegrationId || sequence.linkedinAccountId,
        ]);
      } else {
        setLinkedinIntegrationIds([]);
      }
      setIncludeUnsubscribeLink(sequence.includeUnsubscribeLink ?? true);
      setAutoSend(sequence.autoSend ?? false);
      setRemoveOnReply(sequence.removeOnReply ?? false);
      setTrackOpens(sequence.trackOpens ?? true);
      setTrackClicks(sequence.trackClicks ?? true);
      setSendAsTextOnly(sequence.sendAsTextOnly ?? false);
      setValidateEmails(sequence.validateEmails ?? false);
      setActive(sequence.active !== false);

      // Load CRM sync settings
      setCrmSyncEnabled(sequence.crmSyncEnabled ?? false);
      setCrmIntegrationId(sequence.crmIntegrationId ?? "");

      // Load notification settings
      setNotificationUserIds(sequence.notificationUserIds ?? []);

      // Load scheduling settings
      if (sequence.scheduleSettings) {
        // Ensure timezone is set, default to browser timezone if not present
        const loadedSettings = {
          timezone:
            sequence.scheduleSettings.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            "America/New_York",
          ...sequence.scheduleSettings,
        };
        setScheduleSettings(loadedSettings);
      } else {
        // Legacy support for old schedule format
        const legacyDays = sequence.scheduleDays || [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
        ];
        const legacyStartTime = sequence.scheduleStartTime || "09:00";
        const legacyEndTime = sequence.scheduleEndTime || "17:00";

        const newScheduleSettings: any = {
          timezone:
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            "America/New_York",
        };
        daysOfWeek.forEach((day) => {
          newScheduleSettings[day.value] = {
            enabled: legacyDays.includes(day.value),
            startTime: legacyStartTime,
            endTime: legacyEndTime,
          };
        });
        setScheduleSettings(newScheduleSettings);
      }

      // Load LinkedIn rate limits from JSONB structure or fallback to old individual fields
      const rateLimits = sequence.linkedinRateLimits || {};
      setLinkedinProfileVisitLimit(
        rateLimits.profileVisitLimit || sequence.linkedinProfileVisitLimit || 10
      );
      setLinkedinConnectionRequestLimit(
        rateLimits.connectionRequestLimit ||
          sequence.linkedinConnectionRequestLimit ||
          5
      );
      setLinkedinMessageLimit(
        rateLimits.messageLimit || sequence.linkedinMessageLimit || 8
      );

      // Load email rate limit
      setEmailRateLimit(sequence.emailRateLimit || 2);

      // Load email sender limit
      setEmailSenderLimit(sequence.emailSenderLimit || 50);

      // Load phone settings (for SMS and future voice actions)
      if (
        sequence.phoneIntegrationIds &&
        Array.isArray(sequence.phoneIntegrationIds)
      ) {
        setPhoneIntegrationIds(sequence.phoneIntegrationIds);
      } else {
        setPhoneIntegrationIds([]);
      }

      // Load selected phone numbers
      if (sequence.phoneNumbers && Array.isArray(sequence.phoneNumbers)) {
        setSelectedPhoneNumbers(sequence.phoneNumbers);
      } else {
        setSelectedPhoneNumbers([]);
      }

      setSmsRateLimit(sequence.phoneRateLimit || 2);

      // Load voiceId for voice messages
      setSelectedVoiceId(sequence.voiceId || "");

      // Load prospect value for ROI calculation
      setProspectValue(sequence.prospectValue || 1000);

      // Load removeOnOutcomes for auto-removal based on call intelligence
      if (
        sequence.removeOnOutcomes &&
        Array.isArray(sequence.removeOnOutcomes)
      ) {
        setRemoveOnOutcomes(sequence.removeOnOutcomes);
      } else {
        setRemoveOnOutcomes([]);
      }

      // LinkedIn automation method is always backend_automated (no longer configurable)
    }
  }, [sequence]);

  // Steps are excluded from the list response, so when the editor opens for an
  // existing sequence we fetch the full detail (which includes steps) and
  // populate the steps panel. New sequences (no id) keep their seeded step.
  useEffect(() => {
    if (!open || !sequence?.id) {
      // New sequence (or closed dialog): nothing to fetch, never show the
      // spinner. Clears any stale loading state from a prior cancelled fetch.
      setStepsLoading(false);
      return;
    }
    let cancelled = false;
    setStepsLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(
          `${hostname}/sequences/${sequence.id}`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (cancelled) {
          return;
        }
        const fetchedSteps = (res.data?.steps || []).map((step: any) => {
          const processedStep = {
            ...step,
            actionType: step.actionType || SEQUENCE_ACTION_TYPE_EMAIL,
          };
          if (
            processedStep.actionType ===
              SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST &&
            !processedStep.content &&
            !processedStep.aiPersonalizedInstructions
          ) {
            processedStep.sendWithNote = false;
          }
          return processedStep;
        });
        setSteps(fetchedSteps);
        if (fetchedSteps.length > 0) {
          setSelectedStep(fetchedSteps[0]);
        }
      } catch (err) {
        console.error("Failed to load sequence steps:", err);
      } finally {
        // Only the latest (non-cancelled) fetch clears the loading state, so a
        // superseded request can't hide the spinner for the one still running.
        if (!cancelled) {
          setStepsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sequence?.id, companyId]);

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogTitle>
        <TextField
          sx={{ minWidth: "50%", maxWidth: 400 }}
          label="Sequence Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <IconButton sx={{ float: "right" }} onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Main Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={mainTabValue}
            onChange={(e, v) => setMainTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Builder" />
            <Tab label="Settings" />
            <Tab label="Members" />
            <Tab label="Actions" />
            <Tab label="Metrics" />
            <Tab label="Activity" />
          </Tabs>
        </Box>

        {/* Builder Tab Content */}
        {mainTabValue === 0 && stepsLoading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              minHeight: "60vh",
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading sequence steps…
            </Typography>
          </Box>
        )}
        {mainTabValue === 0 && !stepsLoading && (
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            {/* Left Panel - Step List */}
            <Box
              sx={{
                width: 300,
                mr: 2,
                pr: 1,
                borderRight: "1px solid #ddd",
                minHeight: "90vh",
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  endIcon={<ArrowDropDown />}
                  onClick={(e) => setAddStepAnchorEl(e.currentTarget)}
                  fullWidth
                >
                  Add Step
                </Button>
                <Menu
                  anchorEl={addStepAnchorEl}
                  open={Boolean(addStepAnchorEl)}
                  onClose={() => setAddStepAnchorEl(null)}
                >
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_EMAIL);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <Email sx={{ mr: 1, fontSize: 20 }} />
                    Email
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_SMS);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <Textsms sx={{ mr: 1, fontSize: 20 }} />
                    SMS
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_VOICE_MESSAGE);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <RecordVoiceOver sx={{ mr: 1, fontSize: 20 }} />
                    Voice Message
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_BROWSER_TASK);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <WebAsset sx={{ mr: 1, fontSize: 20 }} />
                    Browser Task
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_HTTP_REQUEST);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <Http sx={{ mr: 1, fontSize: 20 }} />
                    HTTP Request
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <Bot size={20} style={{ marginRight: 8 }} />
                    Agent Conversation
                    <Chip
                      label="Beta"
                      size="small"
                      color="default"
                      sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_AUTO_AGENT);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <Bot size={20} style={{ marginRight: 8 }} />
                    Auto Agent Loop
                    <Chip
                      label="Beta"
                      size="small"
                      color="default"
                      sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <PlaylistAddCheck sx={{ mr: 1, fontSize: 20 }} />
                    Add to Sequence
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_ADD_TO_LIST);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <PlaylistAdd sx={{ mr: 1, fontSize: 20 }} />
                    Add to List
                  </MenuItem>
                  <Divider sx={{ my: 1 }} />
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <img
                      src={require("../../img/prosp.png")}
                      alt="Prosp"
                      style={{ width: 20, height: 20, marginRight: 8 }}
                    />
                    Add to Prosp Campaign
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <img
                      src={require("../../img/heyreach.png")}
                      alt="HeyReach"
                      style={{ width: 20, height: 20, marginRight: 8 }}
                    />
                    Add to HeyReach Campaign
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <img
                      src={require("../../img/salesforge.svg")}
                      alt="Salesforge"
                      style={{ width: 20, height: 20, marginRight: 8 }}
                    />
                    Add to Salesforge Campaign
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_THANKSIO);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <img
                      src={require("../../img/thanksio.png")}
                      alt="Thanks.io"
                      style={{ width: 20, height: 20, marginRight: 8 }}
                    />
                    Thanks.io Direct Mail
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleAddStep(SEQUENCE_ACTION_TYPE_META_ADS_AUDIENCE);
                      setAddStepAnchorEl(null);
                    }}
                  >
                    <img
                      src="https://logos.composio.dev/api/metaads"
                      alt="Meta Ads"
                      style={{ width: 20, height: 20, marginRight: 8 }}
                    />
                    Add to Meta Ads Audience
                  </MenuItem>
                </Menu>
              </Box>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="steps">
                  {(provided: any) => (
                    <List {...provided.droppableProps} ref={provided.innerRef}>
                      {steps.map((step, index) => (
                        <Draggable
                          key={step.id}
                          draggableId={step.id}
                          index={index}
                        >
                          {(provided: any, snapshot: any) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.5 : 1,
                              }}
                              onClick={() => setSelectedStep(step)}
                              selected={
                                selectedStep && selectedStep.id === step.id
                              }
                            >
                              <ListItemIcon>
                                {getActionTypeIcon(step.actionType)}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  step.stepName ||
                                  step.subject ||
                                  "Untitled Step"
                                }
                                secondary={`${getActionTypeLabel(
                                  step.actionType
                                )} • ${
                                  step.daysAfterPrevious === 0
                                    ? "Immediately"
                                    : `${step.daysAfterPrevious} day${
                                        step.daysAfterPrevious === 1 ? "" : "s"
                                      } after ${
                                        index === 0 ? "adding" : "previous"
                                      }`
                                }`}
                              />
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>

            {/* Right Panel - Step Editor */}
            {selectedStep && (
              <Box sx={{ flex: 1, pl: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "center" },
                    mb: 2,
                    gap: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: { xs: 0, sm: 0 } }}>
                    {selectedStep === null
                      ? "Edit Step"
                      : selectedStep.stepName ||
                        selectedStep.subject ||
                        "Untitled Step"}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Tooltip
                      title={!id ? "Save the sequence first to test steps" : ""}
                      arrow
                    >
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={
                            <PlayArrow
                              sx={{ display: { xs: "none", sm: "block" } }}
                            />
                          }
                          onClick={() => setTestStepDialogOpen(true)}
                          fullWidth
                          disabled={!id}
                          sx={{ display: { xs: "block", sm: "inline-flex" } }}
                        >
                          Test
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={
                        <Delete sx={{ display: { xs: "none", sm: "block" } }} />
                      }
                      onClick={() => setOpenDeleteDialog(true)}
                      fullWidth
                      sx={{ display: { xs: "block", sm: "inline-flex" } }}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
                <Box>
                  {/* Action Type Display */}
                  <Box
                    sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Chip
                      icon={getActionTypeIcon(selectedStep.actionType)}
                      label={getActionTypeLabel(selectedStep.actionType)}
                      color="primary"
                      variant="outlined"
                    />
                    {selectedStep.actionType ===
                      SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION && (
                      <Chip
                        label="Beta"
                        size="small"
                        color="default"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Box>

                  {/* Common Settings */}
                  <TextField
                    label="Days After Previous Step"
                    type="number"
                    fullWidth
                    value={selectedStep.daysAfterPrevious}
                    onChange={(e) =>
                      handleSaveStep({
                        ...selectedStep,
                        daysAfterPrevious: e.target.value,
                      })
                    }
                    helperText="Number of days to wait after the previous step executes (0 = same day)"
                    sx={{ mb: 2 }}
                  />

                  {/* Step Name field - for all step types */}
                  <TextField
                    label="Step Name"
                    fullWidth
                    required
                    value={selectedStep.stepName || ""}
                    onChange={(e) =>
                      handleSaveStep({
                        ...selectedStep,
                        stepName: e.target.value,
                      })
                    }
                    helperText="Name to identify this step in the sequence"
                    sx={{ mb: 2 }}
                  />

                  {/* A/B Testing - only for content-based step types */}
                  {(selectedStep.actionType === SEQUENCE_ACTION_TYPE_EMAIL ||
                    selectedStep.actionType === SEQUENCE_ACTION_TYPE_SMS ||
                    selectedStep.actionType === SEQUENCE_ACTION_TYPE_VOICE_MESSAGE ||
                    selectedStep.actionType === SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE) && (
                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedStep.abTestEnabled || false}
                            onChange={(e) => {
                              const enabled = e.target.checked;
                              // When enabling A/B testing, auto-create control variant
                              const variants = enabled && (!selectedStep.variants || selectedStep.variants.length === 0)
                                ? [{
                                    id: uuidv4(),
                                    name: AB_TEST_DEFAULT_VARIANT_NAME,
                                    order: 1,
                                    subject: selectedStep.subject || "",
                                    content: selectedStep.content || "",
                                    weight: 100,
                                    isControl: true,
                                    isActive: true,
                                    aiPersonalized: selectedStep.aiPersonalized || false,
                                    aiPersonalizedInstructions: selectedStep.aiPersonalizedInstructions || "",
                                  }]
                                : selectedStep.variants || [];
                              handleSaveStep({
                                ...selectedStep,
                                abTestEnabled: enabled,
                                variants,
                              });
                            }}
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            A/B Testing
                            <Chip
                              label="Beta"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                backgroundColor: "action.disabledBackground",
                                color: "text.secondary",
                              }}
                            />
                          </Box>
                        }
                      />
                      <FormHelperText>
                        Test different content variations to see which performs best
                      </FormHelperText>

                      {/* A/B Test Editor */}
                      {selectedStep.abTestEnabled && (
                        <Box sx={{ mt: 2 }}>
                          <ABTestEditor
                            step={selectedStep}
                            variants={selectedStep.variants || []}
                            onVariantChange={(variants: SequenceStepVariant[]) =>
                              handleSaveStep({
                                ...selectedStep,
                                variants,
                              })
                            }
                            actionType={selectedStep.actionType}
                            companyId={companyId}
                            sequenceId={id}
                            getToken={getToken}
                            customVariableFields={customUserDataFields}
                            schedulingIntegrations={schedulingIntegrations}
                            heygenIntegrations={heygenIntegrations}
                            onAISuccess={(message) => {
                              if (eventListener) {
                                eventListener({ type: "SET_SUCCESS", message });
                              }
                            }}
                            onAIError={(error) => {
                              if (eventListener) {
                                eventListener({ type: "SET_ERROR", error });
                              }
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Send with message toggle - only for LinkedIn connection requests */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST && (
                    <>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedStep.sendWithNote !== false}
                            onChange={(e) => {
                              handleSaveStep({
                                ...selectedStep,
                                sendWithNote: e.target.checked,
                                content: e.target.checked
                                  ? selectedStep.content
                                  : "",
                                aiPersonalizedInstructions: e.target.checked
                                  ? selectedStep.aiPersonalizedInstructions
                                  : "",
                              });
                            }}
                          />
                        }
                        label="Send with message"
                      />
                      <FormHelperText sx={{ mb: 2 }}>
                        Include a personalized note with the connection request
                      </FormHelperText>
                    </>
                  )}

                  {/* AI Personalization - only for email, SMS, voice, agent conversation, and LinkedIn messages */}
                  {/* Hide when A/B testing is enabled - variants have their own AI settings */}
                  {!selectedStep.abTestEnabled &&
                    (selectedStep.actionType === SEQUENCE_ACTION_TYPE_EMAIL ||
                    selectedStep.actionType === SEQUENCE_ACTION_TYPE_SMS ||
                    selectedStep.actionType ===
                      SEQUENCE_ACTION_TYPE_VOICE_MESSAGE ||
                    selectedStep.actionType ===
                      SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION ||
                    selectedStep.actionType ===
                      SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE ||
                    (selectedStep.actionType ===
                      SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST &&
                      selectedStep.sendWithNote !== false)) && (
                    <>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedStep.aiPersonalized}
                            onChange={() => {
                              handleSaveStep({
                                ...selectedStep,
                                aiPersonalized: !selectedStep.aiPersonalized,
                              });
                            }}
                          />
                        }
                        label="AI Generated"
                      />
                      <FormHelperText sx={{ mb: 2 }}>
                        {selectedStep.actionType === SEQUENCE_ACTION_TYPE_EMAIL
                          ? "AI will generate a unique subject line and email body for each recipient using their profile data and your guidance instructions below."
                          : selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_VOICE_MESSAGE
                          ? "AI will generate a unique voice message script for each recipient using their profile data and your guidance instructions below."
                          : selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION
                          ? "AI will generate unique conversation context for each recipient using their profile data and your guidance instructions below."
                          : "AI will generate a unique message for each recipient using their profile data and your guidance instructions below."}
                      </FormHelperText>
                    </>
                  )}

                  {/* Email Subject field - only for email steps when not AI personalized and not A/B testing */}
                  {selectedStep.actionType === SEQUENCE_ACTION_TYPE_EMAIL &&
                    !selectedStep.aiPersonalized &&
                    !selectedStep.abTestEnabled &&
                    (() => {
                      const emailSteps = steps.filter(
                        (s: any) => s.actionType === SEQUENCE_ACTION_TYPE_EMAIL
                      );
                      const isFirstEmail =
                        emailSteps.findIndex(
                          (s: any) => s.id === selectedStep.id
                        ) === 0;
                      const usePreviousSubject =
                        selectedStep.subject === "<previous-email-subject>";

                      return (
                        <>
                          {!isFirstEmail && (
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={usePreviousSubject}
                                  onChange={(e) =>
                                    handleSaveStep({
                                      ...selectedStep,
                                      subject: e.target.checked
                                        ? "<previous-email-subject>"
                                        : "",
                                    })
                                  }
                                />
                              }
                              label="Reply to previous email thread"
                              sx={{ mb: 1 }}
                            />
                          )}
                          {!usePreviousSubject && (
                            <TextField
                              label="Email Subject"
                              fullWidth
                              required
                              value={selectedStep.subject || ""}
                              onChange={(e) =>
                                handleSaveStep({
                                  ...selectedStep,
                                  subject: e.target.value,
                                })
                              }
                              helperText="Subject line for the email"
                              sx={{ mb: 2 }}
                            />
                          )}
                        </>
                      );
                    })()}

                  {/* Content field - different labels based on action type */}
                  {/* Email Content - use WYSIWYG editor */}
                  {/* Hide when A/B testing is enabled - variants have their own content */}
                  {selectedStep.actionType === SEQUENCE_ACTION_TYPE_EMAIL &&
                    !selectedStep.aiPersonalized &&
                    !selectedStep.abTestEnabled && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Email Content
                        </Typography>
                        <EmailTemplateEditor
                          value={selectedStep.content}
                          subject={selectedStep.subject}
                          onChange={({ subject, content }) => {
                            handleSaveStep({
                              ...selectedStep,
                              ...(subject !== undefined && { subject }),
                              ...(content !== undefined && { content }),
                            });
                          }}
                          height={370}
                          enableVariables
                          enableTemplates
                          enableInsertMenu
                          enableAIGenerate
                          enableSpintax
                          enableAISnippet
                          enableSpamChecker
                          enableMeetingTrackingLink
                          enableHeyGenVideo
                          enableLandingPage
                          schedulingIntegrations={schedulingIntegrations}
                          heygenIntegrations={heygenIntegrations}
                          heygenVideoConfig={selectedStep.actionParameters?.heygenVideo as HeyGenVideoConfig | null}
                          landingPageConfig={selectedStep.actionParameters?.landingPage as LandingPageConfig | null}
                          onHeyGenVideoConfigChange={(config, newContent) => {
                            // Update both config and content in a single state update to avoid race condition
                            handleSaveStep({
                              ...selectedStep,
                              ...(newContent && { content: newContent }),
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                heygenVideo: config,
                              },
                            });
                          }}
                          onLandingPageConfigChange={(config, newContent) => {
                            // Update both config and content in a single state update to avoid race condition
                            handleSaveStep({
                              ...selectedStep,
                              ...(newContent && { content: newContent }),
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                landingPage: config,
                              },
                            });
                          }}
                          onMeetingLinkInserted={(integrationId, newContent) => {
                            handleSaveStep({
                              ...selectedStep,
                              content: newContent,
                              trackCalendarBooking: true,
                              schedulingIntegrationId: integrationId,
                            });
                          }}
                          customVariableFields={customUserDataFields}
                          getToken={getToken}
                          companyId={companyId}
                          sequenceId={id}
                          onAISuccess={(message) => {
                            if (eventListener) {
                              eventListener({ type: "SET_SUCCESS", message });
                            }
                          }}
                          onAIError={(error) => {
                            if (eventListener) {
                              eventListener({ type: "SET_ERROR", error });
                            }
                          }}
                        />
                      </Box>
                    )}

                  {/* Non-email content - use PopOutTextInput */}
                  {/* Hide when A/B testing is enabled - variants have their own content */}
                  {selectedStep.actionType !== SEQUENCE_ACTION_TYPE_EMAIL &&
                    !selectedStep.abTestEnabled && (
                    <Box
                      sx={{
                        mb: 2,
                        display:
                          selectedStep.aiPersonalized ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_HTTP_REQUEST ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_THANKSIO ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_AUTO_AGENT ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_META_ADS_AUDIENCE ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE ||
                          selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_ADD_TO_LIST ||
                          (selectedStep.actionType ===
                            SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST &&
                            selectedStep.sendWithNote === false)
                            ? "none"
                            : "",
                      }}
                    >
                      <PopOutTextInput
                        label={
                          selectedStep.actionType === SEQUENCE_ACTION_TYPE_SMS
                            ? "SMS Message"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_VOICE_MESSAGE
                            ? "Voice Message Script"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION
                            ? "Conversation Context"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST
                            ? "Connection Request Message"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE
                            ? "LinkedIn Message"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_BROWSER_TASK
                            ? "Task Description"
                            : "Content"
                        }
                        multiline
                        rows={10}
                        fullWidth
                        value={selectedStep.content}
                        onChange={(value) =>
                          handleSaveStep({
                            ...selectedStep,
                            content: value,
                          })
                        }
                        helperText={
                          selectedStep.actionType ===
                          SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST
                            ? "LinkedIn limits connection requests to 300 characters"
                            : selectedStep.actionType === SEQUENCE_ACTION_TYPE_SMS
                            ? "SMS messages are limited to 160 characters for single message (longer messages may be split)"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_VOICE_MESSAGE
                            ? "This text will be converted to speech using the voice selected in your sender profile"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION
                            ? "This message will be sent to start the conversation with the agent"
                            : ""
                        }
                        dialogTitle={
                          selectedStep.actionType === SEQUENCE_ACTION_TYPE_SMS
                            ? "Edit SMS Message"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_VOICE_MESSAGE
                            ? "Edit Voice Message Script"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION
                            ? "Edit Initial Message"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST
                            ? "Edit Connection Request Message"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE
                            ? "Edit LinkedIn Message"
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_BROWSER_TASK
                            ? "Edit Task Description"
                            : "Edit Content"
                        }
                        popOutRows={15}
                        maxLength={getMaxLengthForActionType(selectedStep.actionType)}
                        variableFields={standardMemberFields}
                        customVariableFields={customVariableFieldsTyped}
                        aiContext={
                          selectedStep.actionType === SEQUENCE_ACTION_TYPE_SMS
                            ? "This is an SMS message for a sales/marketing sequence. Keep it brief and conversational, ideally under 160 characters."
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_VOICE_MESSAGE
                            ? "This is a voice message script that will be converted to speech. Write it naturally as spoken words, avoiding complex abbreviations."
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST
                            ? "This is a LinkedIn connection request message. Keep it professional, personalized, and under 300 characters."
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE
                            ? "This is a LinkedIn message for outreach. Keep it professional and personalized."
                            : selectedStep.actionType ===
                              SEQUENCE_ACTION_TYPE_BROWSER_TASK
                            ? "This is a task description for a browser automation. Be clear and specific about what actions to perform."
                            : "This is content for an outreach sequence step."
                        }
                        getToken={getToken}
                        companyId={companyId}
                      />
                    </Box>
                  )}

                  {/* AI Guidance Instructions */}
                  {/* Hide when A/B testing is enabled - variants have their own AI instructions */}
                  {!selectedStep.abTestEnabled &&
                    (selectedStep.actionType === SEQUENCE_ACTION_TYPE_EMAIL ||
                    selectedStep.actionType === SEQUENCE_ACTION_TYPE_SMS ||
                    selectedStep.actionType ===
                      SEQUENCE_ACTION_TYPE_VOICE_MESSAGE ||
                    selectedStep.actionType ===
                      SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION ||
                    selectedStep.actionType ===
                      SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE ||
                    (selectedStep.actionType ===
                      SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST &&
                      selectedStep.sendWithNote !== false)) && (
                    <PopOutTextInput
                      label="AI Guidance Instructions"
                      multiline
                      rows={10}
                      fullWidth
                      value={selectedStep.aiPersonalizedInstructions}
                      onChange={(value) =>
                        handleSaveStep({
                          ...selectedStep,
                          aiPersonalizedInstructions: value,
                        })
                      }
                      sx={{
                        mb: 2,
                        display: !selectedStep.aiPersonalized ? "none" : "",
                      }}
                      dialogTitle="Edit AI Guidance Instructions"
                      dialogDescription="Provide instructions for the AI to generate personalized content for each recipient"
                      popOutRows={15}
                      aiContext="These are instructions for an AI to personalize outreach messages. Include guidance on tone, what to emphasize, how to use recipient data, and any constraints."
                      getToken={getToken}
                      companyId={companyId}
                    />
                  )}

                  {/* Calendar Booking Tracking (Email steps only, AI personalization mode) */}
                  {selectedStep.actionType === SEQUENCE_ACTION_TYPE_EMAIL &&
                    selectedStep.aiPersonalized && (
                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedStep.trackCalendarBooking || false}
                            onChange={(e) => {
                              handleSaveStep({
                                ...selectedStep,
                                trackCalendarBooking: e.target.checked,
                                // Clear integration if disabling
                                schedulingIntegrationId: e.target.checked
                                  ? selectedStep.schedulingIntegrationId
                                  : null,
                              });
                            }}
                          />
                        }
                        label="Track Calendar Bookings"
                      />
                      <FormHelperText sx={{ mb: 1 }}>
                        Track when recipients book meetings through your
                        scheduling link
                      </FormHelperText>

                      {selectedStep.trackCalendarBooking && (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                          <InputLabel>Scheduling Integration</InputLabel>
                          <Select
                            value={selectedStep.schedulingIntegrationId || ""}
                            onChange={(e) => {
                              handleSaveStep({
                                ...selectedStep,
                                schedulingIntegrationId: e.target.value || null,
                              });
                            }}
                            label="Scheduling Integration"
                          >
                            <MenuItem value="">
                              <em>Select a scheduling integration</em>
                            </MenuItem>
                            {schedulingIntegrations.map((integration: any) => (
                              <MenuItem
                                key={integration.id}
                                value={integration.id}
                              >
                                {integration.instanceName ||
                                  integration.name ||
                                  "Scheduling"}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {schedulingIntegrations.length === 0
                              ? "No scheduling integrations found. Add one in Settings → Integrations."
                              : "Select the scheduling integration to use for tracking"}
                          </FormHelperText>
                        </FormControl>
                      )}
                    </Box>
                  )}

                  {/* Voice Message Recorded Intro Toggle */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_VOICE_MESSAGE && (
                    <>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              selectedStep.actionParameters
                                ?.recordedMessageIntro || false
                            }
                            onChange={(e) => {
                              handleSaveStep({
                                ...selectedStep,
                                actionParameters: {
                                  ...selectedStep.actionParameters,
                                  recordedMessageIntro: e.target.checked,
                                },
                              });
                            }}
                          />
                        }
                        label="Recorded Message Intro"
                      />
                      <FormHelperText sx={{ mb: 2 }}>
                        Start the voice message with "This is a recorded message
                        from [company name]"
                      </FormHelperText>
                    </>
                  )}

                  {/* Agent Conversation Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION && (
                    <Box sx={{ mb: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Agent</InputLabel>
                        <Select
                          value={selectedStep.actionParameters?.agentId || "default"}
                          onChange={(e) => {
                            handleSaveStep({
                              ...selectedStep,
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                agentId: e.target.value === "default" ? null : e.target.value,
                              },
                            });
                          }}
                          label="Agent"
                        >
                          <MenuItem value="default">
                            <em>--- Use sender profile's agent ---</em>
                          </MenuItem>
                          {agents.map((agent) => (
                            <MenuItem key={agent.id} value={agent.id}>
                              {agent.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Leave blank to use the agent configured on each member's sender profile
                        </FormHelperText>
                      </FormControl>

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Channel</InputLabel>
                        <Select
                          value={
                            selectedStep.actionParameters?.channel || "voice"
                          }
                          onChange={(e) => {
                            handleSaveStep({
                              ...selectedStep,
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                channel: e.target.value,
                              },
                            });
                          }}
                          label="Channel"
                        >
                          <MenuItem value="voice">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Phone sx={{ mr: 1, fontSize: 20 }} />
                              Voice Call
                            </Box>
                          </MenuItem>
                          <MenuItem value="sms">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Textsms sx={{ mr: 1, fontSize: 20 }} />
                              SMS
                            </Box>
                          </MenuItem>
                        </Select>
                        <FormHelperText>
                          {selectedStep.actionParameters?.channel === "voice"
                            ? "Initiate a live AI voice call with the recipient"
                            : "Start an SMS conversation with the recipient"}
                        </FormHelperText>
                      </FormControl>

                      {/* Calendar Booking Tracking for Agent Conversation */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedStep.trackCalendarBooking || false}
                            onChange={(e) => {
                              handleSaveStep({
                                ...selectedStep,
                                trackCalendarBooking: e.target.checked,
                                schedulingIntegrationId: e.target.checked
                                  ? selectedStep.schedulingIntegrationId
                                  : null,
                              });
                            }}
                          />
                        }
                        label="Track Calendar Bookings"
                      />
                      <FormHelperText sx={{ mb: 1 }}>
                        Provide a unique scheduling link to the AI agent during the call.
                        Works best when combined with the SMS tool in voice agent settings.
                      </FormHelperText>

                      {selectedStep.trackCalendarBooking && (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                          <InputLabel>Scheduling Integration</InputLabel>
                          <Select
                            value={selectedStep.schedulingIntegrationId || ""}
                            onChange={(e) => {
                              handleSaveStep({
                                ...selectedStep,
                                schedulingIntegrationId: e.target.value || null,
                              });
                            }}
                            label="Scheduling Integration"
                          >
                            <MenuItem value="">
                              <em>Select a scheduling integration</em>
                            </MenuItem>
                            {schedulingIntegrations.map((integration: any) => (
                              <MenuItem
                                key={integration.id}
                                value={integration.id}
                              >
                                {integration.instanceName ||
                                  integration.name ||
                                  "Scheduling"}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {schedulingIntegrations.length === 0
                              ? "No scheduling integrations found. Add one in Settings → Integrations."
                              : "Select the scheduling integration to use for tracking"}
                          </FormHelperText>
                        </FormControl>
                      )}
                    </Box>
                  )}

                  {/* Browser Task Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_BROWSER_TASK && (
                    <Box sx={{ mb: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Session Type</InputLabel>
                        <Select
                          value={
                            selectedStep.actionParameters?.sessionType || "none"
                          }
                          onChange={(e) => {
                            const newSessionType = e.target.value;
                            handleSaveStep({
                              ...selectedStep,
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                sessionType: newSessionType,
                                // Clear integration ID if switching away from integration mode
                                ...(newSessionType !== "integration"
                                  ? { browserIntegrationId: null }
                                  : {}),
                                // Clear zipcode if switching away from residential
                                ...(newSessionType !== "residential"
                                  ? { zipcode: null }
                                  : {}),
                              },
                            });
                          }}
                          label="Session Type"
                        >
                          <MenuItem value="none">No Session (Basic)</MenuItem>
                          <MenuItem value="residential">
                            Residential Proxy
                          </MenuItem>
                          <MenuItem value="integration">
                            Browser Session Integration
                          </MenuItem>
                        </Select>
                        <FormHelperText>
                          Choose how to run the browser task
                        </FormHelperText>
                      </FormControl>

                      {/* Residential Proxy Configuration */}
                      {selectedStep.actionParameters?.sessionType ===
                        "residential" && (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <TextField
                            label="Zip Code"
                            value={selectedStep.actionParameters?.zipcode || ""}
                            onChange={(e) =>
                              handleSaveStep({
                                ...selectedStep,
                                actionParameters: {
                                  ...selectedStep.actionParameters,
                                  zipcode: e.target.value,
                                },
                              })
                            }
                            placeholder="e.g., 94102"
                            helperText="5-digit US zip code for residential proxy location"
                          />
                        </FormControl>
                      )}

                      {/* Browser Session Integration Configuration */}
                      {selectedStep.actionParameters?.sessionType ===
                        "integration" && (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Browser Session Integration</InputLabel>
                          <Select
                            value={
                              selectedStep.actionParameters
                                ?.browserIntegrationId || ""
                            }
                            onChange={(e) =>
                              handleSaveStep({
                                ...selectedStep,
                                actionParameters: {
                                  ...selectedStep.actionParameters,
                                  browserIntegrationId: e.target.value,
                                },
                              })
                            }
                            label="Browser Session Integration"
                          >
                            {browserIntegrations.length === 0 && (
                              <MenuItem value="" disabled>
                                No browser sessions available
                              </MenuItem>
                            )}
                            {browserIntegrations.map((integration: any) => (
                              <MenuItem
                                key={integration.id}
                                value={integration.id}
                              >
                                {integration.instanceName || integration.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Select the saved browser session to use
                          </FormHelperText>
                        </FormControl>
                      )}

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Browser tasks will execute using the selected
                          configuration. The task description in the "Content"
                          field can include placeholders like {"{firstName}"},{" "}
                          {"{lastName}"}, {"{email}"}, {"{phone}"}, and{" "}
                          {"{linkedinUrl}"} which will be replaced with each
                          sequence member's details.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Prosp Campaign Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN && (
                    <Box sx={{ mb: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Prosp Integration</InputLabel>
                        <Select
                          value={
                            selectedStep.actionParameters?.prospIntegrationId || ""
                          }
                          onChange={(e) =>
                            handleSaveStep({
                              ...selectedStep,
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                prospIntegrationId: e.target.value,
                              },
                            })
                          }
                          label="Prosp Integration"
                        >
                          {prospIntegrations.length === 0 && (
                            <MenuItem value="" disabled>
                              No Prosp integrations available
                            </MenuItem>
                          )}
                          {prospIntegrations.map((integration: any) => (
                            <MenuItem
                              key={integration.id}
                              value={integration.id}
                            >
                              {integration.instanceName || integration.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Select the Prosp integration to use for adding leads
                        </FormHelperText>
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Campaign ID"
                        value={selectedStep.actionParameters?.campaignId || ""}
                        onChange={(e) =>
                          handleSaveStep({
                            ...selectedStep,
                            actionParameters: {
                              ...selectedStep.actionParameters,
                              campaignId: e.target.value,
                            },
                          })
                        }
                        sx={{ mb: 2 }}
                        helperText="The Prosp campaign ID to add leads to. You can find this in the URL when viewing a campaign."
                      />

                      <TextField
                        fullWidth
                        label="List ID (Optional)"
                        value={selectedStep.actionParameters?.listId || ""}
                        onChange={(e) =>
                          handleSaveStep({
                            ...selectedStep,
                            actionParameters: {
                              ...selectedStep.actionParameters,
                              listId: e.target.value,
                            },
                          })
                        }
                        sx={{ mb: 2 }}
                        helperText="Optional Prosp list ID"
                      />

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          This action will add the sequence member to a Prosp
                          campaign. Members must have a LinkedIn URL to be added.
                          Member data (name, email, phone, company, title, and
                          custom fields) will be sent to Prosp.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Salesforge Campaign Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN && (
                    <Box sx={{ mb: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Salesforge Integration</InputLabel>
                        <Select
                          value={
                            selectedStep.actionParameters
                              ?.salesforgeIntegrationId || ""
                          }
                          onChange={(e) =>
                            handleSaveStep({
                              ...selectedStep,
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                salesforgeIntegrationId: e.target.value,
                                workspaceId: "",
                                sequenceId: "",
                              },
                            })
                          }
                          label="Salesforge Integration"
                        >
                          {salesforgeIntegrations.length === 0 && (
                            <MenuItem value="" disabled>
                              No Salesforge integrations available
                            </MenuItem>
                          )}
                          {salesforgeIntegrations.map((integration: any) => (
                            <MenuItem
                              key={integration.id}
                              value={integration.id}
                            >
                              {integration.instanceName || integration.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Select the Salesforge integration to use
                        </FormHelperText>
                      </FormControl>

                      {(() => {
                        const integrationId =
                          selectedStep.actionParameters
                            ?.salesforgeIntegrationId || "";
                        const workspaceId =
                          selectedStep.actionParameters?.workspaceId || "";
                        const workspaces =
                          salesforgeWorkspaces[integrationId] || [];
                        const loadingWorkspaces =
                          salesforgeLoadingWorkspaces[integrationId];
                        const sequencesKey = `${integrationId}:${workspaceId}`;
                        const sequences =
                          salesforgeSequences[sequencesKey] || [];
                        const loadingSequences =
                          salesforgeLoadingSequences[sequencesKey];

                        return (
                          <>
                            <FormControl
                              fullWidth
                              sx={{ mb: 2 }}
                              disabled={!integrationId || loadingWorkspaces}
                            >
                              <InputLabel>Workspace</InputLabel>
                              <Select
                                value={workspaceId}
                                onChange={(e) =>
                                  handleSaveStep({
                                    ...selectedStep,
                                    actionParameters: {
                                      ...selectedStep.actionParameters,
                                      workspaceId: e.target.value,
                                      sequenceId: "",
                                    },
                                  })
                                }
                                label="Workspace"
                              >
                                {!integrationId && (
                                  <MenuItem value="" disabled>
                                    Select an integration first
                                  </MenuItem>
                                )}
                                {integrationId && loadingWorkspaces && (
                                  <MenuItem value="" disabled>
                                    Loading workspaces…
                                  </MenuItem>
                                )}
                                {integrationId &&
                                  !loadingWorkspaces &&
                                  workspaces.length === 0 && (
                                    <MenuItem value="" disabled>
                                      No workspaces found
                                    </MenuItem>
                                  )}
                                {workspaces.map((ws: any) => (
                                  <MenuItem key={ws.id} value={ws.id}>
                                    {ws.name || ws.slug || ws.id}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                Salesforge workspace to enroll contacts into
                              </FormHelperText>
                            </FormControl>

                            <FormControl
                              fullWidth
                              sx={{ mb: 2 }}
                              disabled={!workspaceId || loadingSequences}
                            >
                              <InputLabel>Multichannel Sequence</InputLabel>
                              <Select
                                value={
                                  selectedStep.actionParameters?.sequenceId ||
                                  ""
                                }
                                onChange={(e) =>
                                  handleSaveStep({
                                    ...selectedStep,
                                    actionParameters: {
                                      ...selectedStep.actionParameters,
                                      sequenceId: e.target.value,
                                    },
                                  })
                                }
                                label="Multichannel Sequence"
                              >
                                {!workspaceId && (
                                  <MenuItem value="" disabled>
                                    Select a workspace first
                                  </MenuItem>
                                )}
                                {workspaceId && loadingSequences && (
                                  <MenuItem value="" disabled>
                                    Loading sequences…
                                  </MenuItem>
                                )}
                                {workspaceId &&
                                  !loadingSequences &&
                                  sequences.length === 0 && (
                                    <MenuItem value="" disabled>
                                      No sequences found
                                    </MenuItem>
                                  )}
                                {sequences.map((seq: any) => (
                                  <MenuItem
                                    key={seq.id}
                                    value={String(seq.id)}
                                  >
                                    {seq.name || `Sequence ${seq.id}`}
                                    {seq.status ? ` — ${seq.status}` : ""}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                The Salesforge multichannel sequence to enroll
                                contacts into
                              </FormHelperText>
                            </FormControl>
                          </>
                        );
                      })()}

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          This action will create a contact in Salesforge from
                          the sequence member's data and enroll them in the
                          selected multichannel sequence. An email or LinkedIn
                          URL is required.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* HeyReach Campaign Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN && (
                    <Box sx={{ mb: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>HeyReach Integration</InputLabel>
                        <Select
                          value={
                            selectedStep.actionParameters
                              ?.heyreachIntegrationId || ""
                          }
                          onChange={(e) =>
                            handleSaveStep({
                              ...selectedStep,
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                heyreachIntegrationId: e.target.value,
                              },
                            })
                          }
                          label="HeyReach Integration"
                        >
                          {heyreachIntegrations.length === 0 && (
                            <MenuItem value="" disabled>
                              No HeyReach integrations available
                            </MenuItem>
                          )}
                          {heyreachIntegrations.map((integration: any) => (
                            <MenuItem
                              key={integration.id}
                              value={integration.id}
                            >
                              {integration.instanceName || integration.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Select the HeyReach integration to use for adding
                          leads
                        </FormHelperText>
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Campaign ID"
                        value={selectedStep.actionParameters?.campaignId || ""}
                        onChange={(e) =>
                          handleSaveStep({
                            ...selectedStep,
                            actionParameters: {
                              ...selectedStep.actionParameters,
                              campaignId: e.target.value,
                            },
                          })
                        }
                        sx={{ mb: 2 }}
                        helperText="The HeyReach campaign ID to add leads to. You can find this in your HeyReach dashboard."
                      />

                      <TextField
                        fullWidth
                        label="LinkedIn Account ID (Optional)"
                        value={
                          selectedStep.actionParameters?.linkedInAccountId || ""
                        }
                        onChange={(e) =>
                          handleSaveStep({
                            ...selectedStep,
                            actionParameters: {
                              ...selectedStep.actionParameters,
                              linkedInAccountId: e.target.value,
                            },
                          })
                        }
                        sx={{ mb: 2 }}
                        helperText="Optional LinkedIn account ID to assign the lead to. Leave empty to use default assignment."
                      />

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                selectedStep.actionParameters
                                  ?.resumePausedCampaign || false
                              }
                              onChange={(e) =>
                                handleSaveStep({
                                  ...selectedStep,
                                  actionParameters: {
                                    ...selectedStep.actionParameters,
                                    resumePausedCampaign: e.target.checked,
                                  },
                                })
                              }
                            />
                          }
                          label="Resume paused campaign when adding leads"
                        />
                      </FormControl>

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          This action will add the sequence member to a HeyReach
                          campaign. Members must have a LinkedIn URL to be
                          added. Member data (name, email, company, title, and
                          custom fields) will be sent to HeyReach.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* HTTP Request Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_HTTP_REQUEST && (
                    <Box sx={{ mb: 2 }}>
                      <SequenceHttpRequestForm
                        actionParameters={selectedStep.actionParameters || {}}
                        setActionParameters={(params) =>
                          handleSaveStep({
                            ...selectedStep,
                            actionParameters: params,
                          })
                        }
                        variableFields={standardMemberFields}
                        customVariableFields={customVariableFieldsTyped}
                      />
                    </Box>
                  )}

                  {/* Thanks.io Direct Mail Configuration */}
                  {selectedStep.actionType === SEQUENCE_ACTION_TYPE_THANKSIO && (
                    <Box sx={{ mb: 2 }}>
                      <SequenceThanksioForm
                        actionParameters={selectedStep.actionParameters || {}}
                        setActionParameters={(params) =>
                          handleSaveStep({
                            ...selectedStep,
                            actionParameters: params,
                          })
                        }
                        content={selectedStep.content || ""}
                        setContent={(content) =>
                          handleSaveStep({
                            ...selectedStep,
                            content,
                          })
                        }
                        variableFields={standardMemberFields}
                        customVariableFields={customVariableFieldsTyped}
                        thanksioIntegrations={thanksioIntegrations}
                      />
                    </Box>
                  )}

                  {/* Meta Ads Audience Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_META_ADS_AUDIENCE && (
                    <Box sx={{ mb: 2 }}>
                      <SequenceMetaAdsForm
                        actionParameters={selectedStep.actionParameters || {}}
                        setActionParameters={(params) =>
                          handleSaveStep({
                            ...selectedStep,
                            actionParameters: params,
                          })
                        }
                        metaAdsIntegrations={metaAdsIntegrations}
                        getToken={getToken}
                        hostname={hostname}
                        companyId={companyId}
                      />
                    </Box>
                  )}

                  {/* AutoAgent Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_AUTO_AGENT && (
                    <Box sx={{ mb: 2 }}>
                      <SequenceAutoAgentForm
                        actionParameters={selectedStep.actionParameters || {}}
                        setActionParameters={(params) =>
                          handleSaveStep({
                            ...selectedStep,
                            actionParameters: params,
                          })
                        }
                        agents={agents}
                        content={selectedStep.content || ""}
                        setContent={(content) =>
                          handleSaveStep({
                            ...selectedStep,
                            content: content,
                          })
                        }
                      />
                    </Box>
                  )}

                  {/* Add to Sequence Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE && (
                    <Box sx={{ mb: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Target Sequence</InputLabel>
                        <Select
                          label="Target Sequence"
                          value={
                            selectedStep.actionParameters?.targetSequenceId ||
                            ""
                          }
                          onChange={(e) =>
                            handleSaveStep({
                              ...selectedStep,
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                targetSequenceId: e.target.value,
                              },
                            })
                          }
                        >
                          {availableSequences.length === 0 && (
                            <MenuItem value="" disabled>
                              No sequences available
                            </MenuItem>
                          )}
                          {availableSequences
                            .filter((seq: any) => seq.id !== id)
                            .map((seq: any) => (
                              <MenuItem key={seq.id} value={seq.id}>
                                {seq.name}
                              </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                          The sequence to enroll the member into when this step
                          runs.
                        </FormHelperText>
                      </FormControl>

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>When duplicate found</InputLabel>
                        <Select
                          label="When duplicate found"
                          value={
                            selectedStep.actionParameters?.duplicateHandling ||
                            DUPLICATE_HANDLING_OVERWRITE
                          }
                          onChange={(e) =>
                            handleSaveStep({
                              ...selectedStep,
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                duplicateHandling: e.target.value,
                              },
                            })
                          }
                        >
                          <MenuItem value={DUPLICATE_HANDLING_OVERWRITE}>
                            Update existing member (keep sequence position)
                          </MenuItem>
                          <MenuItem value={DUPLICATE_HANDLING_SKIP}>
                            Skip (don't modify existing member)
                          </MenuItem>
                          <MenuItem value={DUPLICATE_HANDLING_RESET}>
                            Reset existing member to step 1
                          </MenuItem>
                          <MenuItem value={DUPLICATE_HANDLING_ALLOW_DUPLICATES}>
                            Allow duplicates (add new instance)
                          </MenuItem>
                        </Select>
                        <FormHelperText>
                          What to do if a contact with the same email, LinkedIn
                          URL, or phone already exists in the target sequence.
                        </FormHelperText>
                      </FormControl>

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          This step enrolls the current member into another
                          sequence using their contact data and userData fields.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Add to List Configuration */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_ADD_TO_LIST && (
                    <Box sx={{ mb: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Target List</InputLabel>
                        <Select
                          label="Target List"
                          value={
                            selectedStep.actionParameters?.targetListId || ""
                          }
                          onChange={(e) =>
                            handleSaveStep({
                              ...selectedStep,
                              actionParameters: {
                                ...selectedStep.actionParameters,
                                targetListId: e.target.value,
                              },
                            })
                          }
                        >
                          {availableLists.length === 0 && (
                            <MenuItem value="" disabled>
                              No lists available
                            </MenuItem>
                          )}
                          {availableLists.map((list: any) => (
                            <MenuItem key={list.id} value={list.id}>
                              {list.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          The list to add the member to as a new row when this
                          step runs.
                        </FormHelperText>
                      </FormControl>

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          If the member came from a list, their row data (except
                          action and lookup columns) is copied exactly.
                          Otherwise their contact fields and userData are added.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* LinkedIn Profile Visit Info */}
                  {selectedStep.actionType ===
                    SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        This action will visit the LinkedIn profile of each
                        sequence member. Profile visits help increase visibility
                        and may encourage the person to view your profile in
                        return.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Settings Tab Content */}
        {mainTabValue === 1 && (
          <Box sx={{ maxWidth: 800, mx: "auto" }}>
            {/* General Settings Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              General Settings
            </Typography>
            <Box
              sx={{ mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}
            >
              {id && (
                <>
                  <FormControlLabel
                    sx={{ mb: 1, display: "block" }}
                    control={
                      <Switch
                        checked={active}
                        onChange={(e) => toggleSequenceActive(e.target.checked)}
                      />
                    }
                    label="Status"
                  />
                  <FormHelperText sx={{ mb: 2 }}>
                    Control whether this sequence is active and can execute
                    actions. Inactive sequences will not process new members or
                    send messages.
                  </FormHelperText>
                </>
              )}
              <Box sx={{ mb: 2 }}>
                <SearchableSenderProfileSelect
                  multiple
                  profiles={profiles}
                  selectedIds={senderProfileIds}
                  onChange={(newProfileIds) => {
                    setSenderProfileIds(newProfileIds);

                    // Update legacy single profile for backward compatibility
                    if (newProfileIds.length > 0) {
                      setSenderProfileId(newProfileIds[0]);
                      const selectedProfile = profiles.find(
                        (profile: any) => profile.id === newProfileIds[0]
                      );
                      setSelectedProfile(selectedProfile);
                    } else {
                      setSenderProfileId("");
                      setSelectedProfile(null);
                    }
                  }}
                  label="Sender Profiles"
                  loading={profilesLoading}
                  helperText="Select sender profiles to use for this sequence. For each member, we'll randomly select one sender profile and use it for the entire sequence. The sender profile's settings (email account, LinkedIn account, phone number, voice) will be used first when available for each action type."
                />
              </Box>

              <FormControlLabel
                sx={{ mb: 1, display: "block" }}
                control={
                  <Switch
                    checked={autoSend}
                    onChange={(e) => setAutoSend(e.target.checked)}
                  />
                }
                label="Auto Approve"
              />
              <FormHelperText sx={{ mb: 2 }}>
                Automatically approve and send messages when scheduled. When
                disabled, messages require manual approval before sending.
              </FormHelperText>

              <TextField
                fullWidth
                label="Prospect Value ($)"
                type="number"
                value={prospectValue}
                onChange={(e) => setProspectValue(Number(e.target.value) || 0)}
                inputProps={{ min: 0, step: 100 }}
                sx={{ mb: 1 }}
              />
              <FormHelperText sx={{ mb: 2 }}>
                The estimated value per positive outcome (reply or meeting
                booking). Used to calculate ROI metrics for this sequence.
              </FormHelperText>
            </Box>

            {/* Email Settings Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Email Settings
            </Typography>
            <Box
              sx={{ mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}
            >
              <FormControlLabel
                sx={{ mb: 1, display: "block" }}
                control={
                  <Switch
                    checked={removeOnReply}
                    onChange={(e) => setRemoveOnReply(e.target.checked)}
                  />
                }
                label="Remove on Reply"
              />
              <FormHelperText sx={{ mb: 2 }}>
                Automatically remove members from the sequence when they reply
                to any message
              </FormHelperText>

              <FormControlLabel
                sx={{ mb: 1, display: "block" }}
                control={
                  <Switch
                    checked={includeUnsubscribeLink}
                    onChange={(e) =>
                      setIncludeUnsubscribeLink(e.target.checked)
                    }
                    disabled={sendAsTextOnly}
                  />
                }
                label="Include Unsubscribe Link"
              />
              <FormHelperText sx={{ mb: 2 }}>
                Include an unsubscribe link in email footers for compliance
                {sendAsTextOnly && " (disabled when sending as text-only)"}
              </FormHelperText>

              <FormControlLabel
                sx={{ mb: 1, display: "block" }}
                control={
                  <Switch
                    checked={trackOpens}
                    onChange={(e) => setTrackOpens(e.target.checked)}
                    disabled={sendAsTextOnly}
                  />
                }
                label="Track Email Opens"
              />
              <FormHelperText sx={{ mb: 2 }}>
                Track when recipients open emails sent from this sequence
                {sendAsTextOnly && " (disabled when sending as text-only)"}
              </FormHelperText>

              <FormControlLabel
                sx={{ mb: 1, display: "block" }}
                control={
                  <Switch
                    checked={trackClicks}
                    onChange={(e) => setTrackClicks(e.target.checked)}
                    disabled={sendAsTextOnly}
                  />
                }
                label="Track Link Clicks"
              />
              <FormHelperText sx={{ mb: 2 }}>
                Track when recipients click links in emails sent from this
                sequence
                {sendAsTextOnly && " (disabled when sending as text-only)"}
              </FormHelperText>

              <FormControlLabel
                sx={{ mb: 1, display: "block" }}
                control={
                  <Switch
                    checked={sendAsTextOnly}
                    onChange={(e) => {
                      const isTextOnly = e.target.checked;
                      setSendAsTextOnly(isTextOnly);
                      // Automatically turn off tracking and unsubscribe when enabling text-only
                      if (isTextOnly) {
                        setTrackOpens(false);
                        setTrackClicks(false);
                        setIncludeUnsubscribeLink(false);
                      }
                    }}
                  />
                }
                label="Send Emails as Text-Only (No HTML)"
              />
              <FormHelperText sx={{ mb: 2 }}>
                Send emails as plain text instead of HTML. This optimizes
                deliverability but will disable unsubscribe links, click and
                open tracking if enabled.
              </FormHelperText>

              <FormControlLabel
                sx={{ mb: 1, display: "block" }}
                control={
                  <Switch
                    checked={validateEmails}
                    onChange={(e) => setValidateEmails(e.target.checked)}
                  />
                }
                label="Validate Email Addresses (1 credit per email)"
              />
              <FormHelperText sx={{ mb: 2 }}>
                Check email addresses before sending to detect invalid or
                likely-to-bounce emails. Validates email format and verifies the
                domain has valid MX records. Invalid emails will be marked as
                failed and you can review them. Costs 1 credit per email
                validation.
              </FormHelperText>

              {/* Email Rate Limit */}
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: "medium" }}
              >
                Daily Email Rate Limit
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Emails per Recipient:{" "}
                  <strong>{emailRateLimit} per day</strong>
                </Typography>
                <Slider
                  value={emailRateLimit}
                  onChange={(e, value) => setEmailRateLimit(value as number)}
                  min={1}
                  max={5}
                  step={1}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 2, label: "2" },
                    { value: 3, label: "3" },
                    { value: 4, label: "4" },
                    { value: 5, label: "5" },
                  ]}
                  sx={{ mb: 2 }}
                />
                <FormHelperText>
                  Maximum number of emails that can be sent to the same email
                  address per day across all sequences. This helps prevent spam
                  and maintains deliverability.
                </FormHelperText>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Emails per Sender Account:{" "}
                  <strong
                    style={{
                      color: emailSenderLimit > 30 ? "#f57c00" : "inherit",
                    }}
                  >
                    {emailSenderLimit} per day
                  </strong>
                </Typography>
                <Slider
                  value={emailSenderLimit}
                  onChange={(e, value) => setEmailSenderLimit(value as number)}
                  min={1}
                  max={40}
                  step={1}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 10, label: "10" },
                    { value: 20, label: "20" },
                    { value: 30, label: "30" },
                    { value: 40, label: "40" },
                  ]}
                  sx={{
                    mb: 2,
                    "& .MuiSlider-thumb": {
                      backgroundColor:
                        emailSenderLimit > 30 ? "#f57c00" : undefined,
                    },
                    "& .MuiSlider-track": {
                      backgroundColor:
                        emailSenderLimit > 30 ? "#f57c00" : undefined,
                    },
                    "& .MuiSlider-rail": {
                      backgroundColor:
                        emailSenderLimit > 30 ? "#ffb74d" : undefined,
                    },
                  }}
                />
                <FormHelperText
                  sx={{
                    color: emailSenderLimit > 30 ? "#f57c00" : undefined,
                    fontWeight: emailSenderLimit > 30 ? "medium" : undefined,
                  }}
                >
                  Maximum number of emails that can be sent from each sender
                  account per day. This helps protect sender reputation and
                  prevents your email accounts from being flagged as spam.
                  {emailSenderLimit > 30 &&
                    " ⚠️ Values over 30 may increase spam risk."}
                </FormHelperText>
              </Box>
            </Box>

            {/* CRM Sync Settings Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              CRM Sync Settings
            </Typography>
            <Box
              sx={{ mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sync sequence activity to your CRM for contacts that already
                exist in the selected CRM.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={crmSyncEnabled}
                    onChange={(e) => {
                      setCrmSyncEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setCrmIntegrationId("");
                      }
                    }}
                  />
                }
                label="Enable CRM Sync"
                sx={{ mb: 2, display: "block" }}
              />

              {crmSyncEnabled && (
                <>
                  <CrmIntegrationSelect
                    value={crmIntegrationId}
                    onChange={setCrmIntegrationId}
                    getToken={getToken}
                    companyId={companyId}
                    helperText=""
                  />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, mt: 2 }}
                  >
                    <strong>What syncs:</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="ul"
                    sx={{ pl: 2, mt: 0 }}
                  >
                    <li>
                      <strong>Positive replies:</strong> When a contact sends a
                      positive reply, a new contact is created (or updated) in
                      your CRM and the reply is logged as an activity
                    </li>
                    <li>
                      <strong>Sequence steps:</strong> Emails, SMS, voice
                      messages, and LinkedIn actions are logged as activities
                      for contacts that already exist in your CRM
                    </li>
                  </Typography>
                </>
              )}
            </Box>

            {/* Notification Settings Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Notification Settings
            </Typography>
            <Box
              sx={{ mb: 4, p: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which team members should receive notifications for this sequence.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Notifications are sent for:
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2, color: "text.secondary" }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  Daily activity summaries (pending approvals, auto-sent messages)
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Email replies with positive sentiment or interest (real-time alerts with classification)
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Voice calls with live human conversations (real-time alerts with full transcript and call intelligence)
                </Typography>
              </Box>

              <MultiCollaboratorSelect
                companyId={companyId}
                getToken={getToken}
                value={notificationUserIds}
                onChange={(userIds) => setNotificationUserIds(userIds)}
                label="Notification Recipients"
                helperText="If no users are selected, notifications will be sent to the sequence owner."
              />
            </Box>

            {/* Phone Settings Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Phone Settings
            </Typography>
            <Box
              sx={{ mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}
            >
              {Array.isArray(twilioIntegrations) &&
                twilioIntegrations.length > 0 && (
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Twilio Integrations</InputLabel>
                    <Select
                      multiple
                      label="Twilio Integrations"
                      value={phoneIntegrationIds}
                      onChange={(
                        event: SelectChangeEvent<typeof phoneIntegrationIds>
                      ) => {
                        const value = event.target.value;
                        const newIntegrationIds =
                          typeof value === "string" ? value.split(",") : value;
                        setPhoneIntegrationIds(newIntegrationIds);
                      }}
                      input={<OutlinedInput label="Twilio Integrations" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((integrationId) => {
                            const integration = twilioIntegrations.find(
                              (i: any) => i.id === integrationId
                            );
                            const displayName =
                              integration?.instanceName ||
                              integration?.parameters?.name ||
                              integration?.name ||
                              "Twilio Account";
                            return (
                              <Chip
                                key={integrationId}
                                label={displayName}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {twilioIntegrations.map((integration) => (
                        <MenuItem key={integration.id} value={integration.id}>
                          <Checkbox
                            checked={
                              phoneIntegrationIds.indexOf(integration.id) > -1
                            }
                          />
                          <ListItemText
                            primary={
                              integration.instanceName ||
                              integration.parameters?.name ||
                              integration.name ||
                              "Twilio Account"
                            }
                            secondary={
                              integration.parameters?.accountSid
                                ? `Account SID: ${integration.parameters.accountSid.substring(
                                    0,
                                    12
                                  )}...`
                                : `ID: ${integration.id.substring(0, 8)}`
                            }
                          />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select one or more Twilio integrations to enable phone
                      actions in this sequence. These integrations will be used
                      as a fallback if a sender profile doesn't have a phone
                      number configured.
                    </FormHelperText>
                  </FormControl>
                )}

              {/* Phone Number Selection */}
              {Array.isArray(availablePhoneNumbers) &&
                availablePhoneNumbers.length > 0 && (
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Phone Numbers</InputLabel>
                    <Select
                      multiple
                      label="Phone Numbers"
                      value={selectedPhoneNumbers}
                      onChange={(
                        event: SelectChangeEvent<typeof selectedPhoneNumbers>
                      ) => {
                        const value = event.target.value;
                        const newPhoneNumbers =
                          typeof value === "string" ? value.split(",") : value;
                        setSelectedPhoneNumbers(newPhoneNumbers);
                      }}
                      input={<OutlinedInput label="Phone Numbers" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((phoneNumber) => {
                            const number = availablePhoneNumbers.find(
                              (n: any) => n.phoneNumber === phoneNumber
                            );
                            return (
                              <Chip
                                key={phoneNumber}
                                label={
                                  number ? number.friendlyName : phoneNumber
                                }
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {availablePhoneNumbers.map((number) => (
                        <MenuItem
                          key={number.phoneNumber}
                          value={number.phoneNumber}
                        >
                          <Checkbox
                            checked={
                              selectedPhoneNumbers.indexOf(number.phoneNumber) >
                              -1
                            }
                          />
                          <ListItemText
                            primary={number.friendlyName}
                            secondary={`${number.phoneNumber} (${number.integrationName})`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select one or more phone numbers to use for phone actions
                      in this sequence. These numbers will be used as a fallback
                      if a sender profile doesn't have a phone number
                      configured.
                    </FormHelperText>
                  </FormControl>
                )}

              {(!twilioIntegrations || twilioIntegrations.length === 0) && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No Twilio integrations configured. Set up Twilio
                    integrations in the Integrations page to enable phone
                    actions.
                  </Typography>
                </Box>
              )}

              {/* Phone Rate Limit */}
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: "medium" }}
              >
                Daily Phone Action Rate Limit
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Phone Actions per Recipient:{" "}
                  <strong>{smsRateLimit} per day</strong>
                </Typography>
                <Slider
                  value={smsRateLimit}
                  onChange={(e, value) => setSmsRateLimit(value as number)}
                  min={1}
                  max={5}
                  step={1}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 2, label: "2" },
                    { value: 3, label: "3" },
                    { value: 4, label: "4" },
                    { value: 5, label: "5" },
                  ]}
                  sx={{ mb: 2 }}
                />
                <FormHelperText>
                  Maximum number of phone actions that can be sent to the same
                  phone number per day across all sequences. This helps prevent
                  spam and maintains compliance.
                </FormHelperText>
              </Box>

              {/* Voice Selection */}
              {Array.isArray(voices) && voices.length > 0 && (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: "medium" }}
                  >
                    Default Voice for Voice Messages
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Voice</InputLabel>
                    <Select
                      label="Voice"
                      value={selectedVoiceId}
                      onChange={(e) => setSelectedVoiceId(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Use sender profile voice (or default)</em>
                      </MenuItem>
                      {voices.map((voice: any) => (
                        <MenuItem key={voice.id} value={voice.id}>
                          {voice.name}
                          {voice.category ? ` (${voice.category})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select a fallback voice for voice actions in this
                      sequence. The sender profile's voice will be used first if
                      configured. If not available, this sequence-level voice
                      will be used. If neither are set, the system default voice
                      will be used.
                    </FormHelperText>
                  </FormControl>
                </>
              )}

              {/* Remove on Outcomes - Multi-select for conversation intelligence outcomes */}
              <Typography
                variant="subtitle1"
                sx={{ mt: 3, mb: 2, fontWeight: "medium" }}
              >
                Auto-Remove on Conversation Outcome
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Remove on Outcomes</InputLabel>
                <Select
                  multiple
                  label="Remove on Outcomes"
                  value={removeOnOutcomes}
                  onChange={(event: SelectChangeEvent<typeof removeOnOutcomes>) => {
                    const value = event.target.value;
                    const newOutcomes =
                      typeof value === "string" ? value.split(",") : value;
                    setRemoveOnOutcomes(newOutcomes);
                  }}
                  input={<OutlinedInput label="Remove on Outcomes" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((outcome) => {
                        const outcomeInfo = CONVERSATION_OUTCOMES.find(
                          (o) => o.value === outcome
                        );
                        return (
                          <Chip
                            key={outcome}
                            label={outcomeInfo?.label || outcome}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {CONVERSATION_OUTCOMES.map((outcome) => (
                    <MenuItem key={outcome.value} value={outcome.value}>
                      <Checkbox
                        checked={removeOnOutcomes.indexOf(outcome.value) > -1}
                      />
                      <ListItemText primary={outcome.label} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  When a phone conversation (voice call or SMS) results in one
                  of these outcomes (detected via conversation intelligence),
                  the member will be automatically removed from this sequence.
                  For example, select "Meeting Booked" to auto-remove members
                  after a successful booking, or "Not Interested" to stop
                  contacting uninterested prospects.
                </FormHelperText>
              </FormControl>
            </Box>

            {/* LinkedIn Settings Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              LinkedIn Settings
            </Typography>
            <Box
              sx={{ mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}
            >
              {Array.isArray(linkedinIntegrations) &&
                linkedinIntegrations.length > 0 && (
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>LinkedIn Integrations</InputLabel>
                    <Select
                      multiple
                      label="LinkedIn Integrations"
                      value={linkedinIntegrationIds}
                      onChange={(
                        event: SelectChangeEvent<typeof linkedinIntegrationIds>
                      ) => {
                        const value = event.target.value;
                        const newIntegrationIds =
                          typeof value === "string" ? value.split(",") : value;
                        setLinkedinIntegrationIds(newIntegrationIds);
                      }}
                      input={<OutlinedInput label="LinkedIn Integrations" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((integrationId) => {
                            const integration = linkedinIntegrations.find(
                              (i: any) => i.id === integrationId
                            );
                            const displayName =
                              integration?.instanceName ||
                              integration?.parameters?.profileName ||
                              integration?.parameters?.name ||
                              integration?.name ||
                              "LinkedIn Account";
                            return (
                              <Chip
                                key={integrationId}
                                label={displayName}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {linkedinIntegrations.map((integration) => (
                        <MenuItem key={integration.id} value={integration.id}>
                          <Checkbox
                            checked={
                              linkedinIntegrationIds.indexOf(integration.id) >
                              -1
                            }
                          />
                          <ListItemText
                            primary={
                              integration.instanceName ||
                              integration.parameters?.profileName ||
                              integration.parameters?.name ||
                              integration.name ||
                              "LinkedIn Account"
                            }
                            secondary={
                              integration.parameters?.email ||
                              integration.parameters?.linkedinUrl ||
                              `ID: ${integration.id.substring(0, 8)}`
                            }
                          />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select one or more LinkedIn integrations as a fallback for
                      LinkedIn actions. If a sender profile has its own LinkedIn
                      integration set, that will be used first. If not, these
                      sequence-level integrations will be used. If neither are
                      set, LinkedIn actions cannot be performed.
                    </FormHelperText>
                  </FormControl>
                )}

              {(!linkedinIntegrations || linkedinIntegrations.length === 0) && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No LinkedIn integrations configured. Set up LinkedIn
                    integrations in the Integrations page to enable LinkedIn
                    automation.
                  </Typography>
                </Box>
              )}

              {/* LinkedIn Rate Limits */}
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: "medium" }}
              >
                Daily Action Limits
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Profile Visits:{" "}
                  <strong>{linkedinProfileVisitLimit} per day</strong>
                </Typography>
                <Slider
                  value={linkedinProfileVisitLimit}
                  onChange={(e, value) =>
                    setLinkedinProfileVisitLimit(value as number)
                  }
                  min={1}
                  max={30}
                  step={1}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 15, label: "15" },
                    { value: 30, label: "30" },
                  ]}
                  sx={{ mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Connection Requests:{" "}
                  <strong>{linkedinConnectionRequestLimit} per day</strong>
                </Typography>
                <Slider
                  value={linkedinConnectionRequestLimit}
                  onChange={(e, value) =>
                    setLinkedinConnectionRequestLimit(value as number)
                  }
                  min={1}
                  max={30}
                  step={1}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 15, label: "15" },
                    { value: 30, label: "30" },
                  ]}
                  sx={{ mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  LinkedIn Messages:{" "}
                  <strong>{linkedinMessageLimit} per day</strong>
                </Typography>
                <Slider
                  value={linkedinMessageLimit}
                  onChange={(e, value) =>
                    setLinkedinMessageLimit(value as number)
                  }
                  min={1}
                  max={30}
                  step={1}
                  marks={[
                    { value: 1, label: "1" },
                    { value: 15, label: "15" },
                    { value: 30, label: "30" },
                  ]}
                  sx={{ mb: 2 }}
                />
                <FormHelperText>
                  Daily limits help maintain natural interaction patterns and
                  avoid LinkedIn restrictions
                </FormHelperText>
              </Box>
            </Box>

            {/* Schedule Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Schedule
            </Typography>
            <Box sx={{ mb: 4 }}>
              <SendScheduleEditor
                value={scheduleSettings as any}
                onChange={(next) => setScheduleSettings(next as any)}
                helperText="Sequence actions will only be executed during the specified time windows on enabled days"
              />
            </Box>
          </Box>
        )}

        {/* Members Tab Content */}
        {mainTabValue === 2 && (
          <SequenceMembersView
            companyId={companyId}
            sequence={id ? { id, name, steps } : null}
            getToken={getToken}
            eventListener={eventListener}
            showTitle={false}
            showActions={true}
          />
        )}

        {/* Actions Tab Content */}
        {mainTabValue === 3 && (
          <SequenceActions
            companyId={companyId}
            sequence={id ? { id, name, steps } : null}
            getToken={getToken}
            eventListener={eventListener}
            showTitle={false}
            initialFilter="all"
          />
        )}

        {/* Metrics Tab Content */}
        {mainTabValue === 4 && id && (
          <SequenceMetrics
            sequenceId={id}
            companyId={companyId}
            getToken={getToken}
            eventListener={eventListener || (() => {})}
            showTitle={false}
          />
        )}

        {/* Activity Tab Content */}
        {mainTabValue === 5 && (
          <SequenceActivityView
            companyId={companyId}
            sequence={id ? { id, name } : null}
            getToken={getToken}
            showTitle={false}
          />
        )}
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Step</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this step? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteStep} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Step Dialog */}
      <Dialog
        key={testStepDialogOpen ? "test-dialog-open" : "test-dialog-closed"}
        open={testStepDialogOpen}
        onClose={handleCloseTestDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Test Step:{" "}
          {selectedStep?.stepName || selectedStep?.subject || "Untitled Step"}
          <IconButton
            sx={{ position: "absolute", right: 8, top: 8 }}
            onClick={handleCloseTestDialog}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test this step to preview the AI-generated content before sending.
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: useTestUser ? "action.selected" : "action.hover",
              border: 1,
              borderColor: useTestUser ? "primary.main" : "divider",
              cursor: "pointer",
            }}
            onClick={() => {
              setUseTestUser(true);
              setTestStepSelectedMember(null);
              setTestStepMemberId("");
              setTestStepResult(null);
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Use Test User</Typography>
              <Typography variant="body2" color="text.secondary">
                Jane Smith, VP of Marketing at Acme Corporation
              </Typography>
            </Box>
            {useTestUser && (
              <Chip label="Selected" size="small" color="primary" />
            )}
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, textAlign: "center" }}
          >
            or select a sequence member
          </Typography>

          <Autocomplete
            fullWidth
            value={testStepSelectedMember}
            onChange={(event, newValue) => {
              setTestStepSelectedMember(newValue);
              setTestStepMemberId(newValue ? newValue.id : "");
              setUseTestUser(false);
              setTestStepResult(null);
            }}
            onInputChange={(event, newInputValue) => {
              setMemberSearchQuery(newInputValue);
            }}
            options={sequenceMembers}
            loading={memberSearchLoading}
            getOptionLabel={(option: any) =>
              `${option.firstName} ${option.lastName} (${option.email})`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Member"
                helperText="Choose a member to generate personalized content"
              />
            )}
            sx={{ mb: 3 }}
          />

          {testStepResult && (
            <Box>
              {selectedStep?.actionType === SEQUENCE_ACTION_TYPE_EMAIL && (
                <>
                  <TextField
                    fullWidth
                    label="Subject"
                    value={testStepResult.subject || ""}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Content
                  </Typography>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                      minHeight: 200,
                      maxHeight: 400,
                      overflow: "auto",
                      backgroundColor: "background.paper",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(testStepResult.content || ""),
                    }}
                  />
                </>
              )}
              {(selectedStep?.actionType === SEQUENCE_ACTION_TYPE_SMS ||
                selectedStep?.actionType ===
                  SEQUENCE_ACTION_TYPE_VOICE_MESSAGE ||
                selectedStep?.actionType ===
                  SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE ||
                selectedStep?.actionType ===
                  SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST) && (
                <TextField
                  fullWidth
                  label={
                    selectedStep?.actionType === SEQUENCE_ACTION_TYPE_EMAIL
                      ? "Email Content"
                      : selectedStep?.actionType === SEQUENCE_ACTION_TYPE_SMS
                      ? "SMS Message"
                      : selectedStep?.actionType ===
                        SEQUENCE_ACTION_TYPE_VOICE_MESSAGE
                      ? "Voice Message Script"
                      : selectedStep?.actionType ===
                        SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST
                      ? "Connection Request Message"
                      : "LinkedIn Message"
                  }
                  value={testStepResult.content || ""}
                  multiline
                  rows={8}
                  InputProps={{ readOnly: true }}
                />
              )}
              {selectedStep?.actionType ===
                SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT && (
                <Typography variant="body2" color="text.secondary">
                  This step will visit the LinkedIn profile of the selected
                  member. No content is generated for profile visits.
                </Typography>
              )}
              {selectedStep?.actionType ===
                SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION && (
                <>
                  <TextField
                    fullWidth
                    label="Opening Line"
                    value={testStepResult.openingLine || ""}
                    multiline
                    rows={3}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Conversation Context"
                    value={testStepResult.conversationContext || ""}
                    multiline
                    rows={8}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Voicemail Message"
                    value={testStepResult.voicemailMessage || ""}
                    multiline
                    rows={4}
                    InputProps={{ readOnly: true }}
                  />
                </>
              )}
            </Box>
          )}

          {testStepResult &&
            selectedStep?.actionType === SEQUENCE_ACTION_TYPE_EMAIL && (
              <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: "divider" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  Send Test Email
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Send this AI-generated email to your inbox to see how it
                  looks.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <TextField
                    fullWidth
                    label="Test Email Address"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="your@email.com"
                    type="email"
                    helperText="Enter your email to receive a test message"
                  />
                  <LoadingButton
                    loading={sendingTestEmail}
                    onClick={handleSendTestEmail}
                    variant="contained"
                    sx={{ minWidth: 100, height: 56 }}
                  >
                    Send
                  </LoadingButton>
                </Box>
              </Box>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestDialog}>Close</Button>
          <LoadingButton
            loading={testStepLoading}
            onClick={handleTestStep}
            variant="contained"
            disabled={!testStepMemberId && !useTestUser}
          >
            Generate Preview
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <LoadingButton loading={loading} onClick={onSubmit} variant="contained">
          Save Sequence
        </LoadingButton>
      </DialogActions>

      {/* Save Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSaveSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Sequence saved successfully
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
